import type { LectureSourceUploadContext } from "../constants/GlobalTypesAndEnumsAndConstants";
import { type LandingIngestionOptions, type LandingIngestionOutcome } from "../pages/home-page/landingHandoffContext";
import { revealInitialHomeWhenSettled } from "../utils/home-startup-presentation";
import { serializeMobileAppEvent } from "../backend/utils/mobile-app-events";
import { receiveNativeInterfaceLanguageContext } from "../i18n/nativeInterfaceLanguages";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CardData,
  CardType,
  ContentMediumType,
  DeckObject,
  EventSentToMobileApp,
  EventReceivedFromMobileApp,
  SendOrReceiveMobileAppEvent,
  SignUpAndOnboardingModalActivatedReason,
  SignUpAndOnboardingModalView,
  UserSubscriptionTimePeriod,
  UnderstandingRatingsForNonAuthenticatedUser,
  UserData,
  UserSubscriptionPlan,
  ManagerSubscriptionAccount,
  JUNGLE_CLOUDFLARE_BASE_URL,
  examplePageGeneratedIds,
  mimeTypesFiles,
  UserType,
  GooglePlaySubscriptionProductDetails,
  AppleSubscriptionProductDetails,
  FileUploadWithDeckData,
  STREAKS_COLLECTION_NAME,
  NewFeaturesForWhatsNewModal,
  isHealthRelatedStudentType,
} from "../constants/GlobalTypesAndEnumsAndConstants";
import {
  cardStore,
  generateCardsStore,
  navigationWithStatesStore,
  pageStore,
  signUpAndOnboardingModalStore,
  useGeneratedCardsStore,
  usePDFViewerStore,
  useReviewDeckStore,
  useStreakStore,
  userStore,
  legacyReduxStore,
  useReviewCheckpointChallengeStore,
} from "../zustand/globalStore";
import {
  buildCoveredPassageChallengeContext,
  selectRandomCoveredConceptSource,
} from "../pages/review-deck-page/reviewCheckpointChallengeHelpers";
import { resolveVoiceOpeningReplyLanguage } from "../voice/core/replyLanguage";
import { getSubscriptionBillingIdentity } from "../utils/subscription-billing";
import { createSubscriptionRequestAuthority } from "../utils/subscription-request-authority";
import { SidePanelOrchestrator } from "../zustand/sidePanelStores";
import { useReviewProgressMarksStore } from "../zustand/reviewProgressMarksStore";
import {
  doesFirstReviewEndWithClinicalExamStation,
  doesFirstReviewEndWithVoiceCheck,
  isInTheFirstReviewStationTest,
} from "../first-experience-tests/firstExperienceGelRules";
import { useClinicalExamStationStore } from "../zustand/clinicalExamStationStore";
import { isClinicalExamPracticeEnabledForUser } from "../clinical-exams/clinicalExamPracticeAvailability";
import {
  isAPageAfterTheFirstReviewOnScreen,
  isTheExamQuestionStillToAskAfterTheCheckpoint,
  takeTheExamSavedBeforeTheFirstReviewsCheckpoint,
  openTheNextPageAfterTheFirstReview,
} from "../pages/review-deck-page/first-user-experience/pagesAfterTheFirstReview";
import {
  FIRST_REVIEW_OSCE_STATION_TEST,
  FIRST_REVIEW_VOICE_CHECK_TEST,
  getFirstExperienceTestVariant,
} from "../first-experience-tests/firstExperienceTests";
import {
  exportCardsToAnkiFile,
  filterCardsWithMistakes,
  getContentMediumTypeFromFileNameOrPageUrl,
  getCookie,
  getDataFromUrl,
  getPresignedUrlForImageKey,
  getStripeManageAccountSessionForUser,
  getUserSubscriptionPlanAndLimits,
  hasUserSubscribedBefore,
  showAlertForUnrecognizedSubscriptionBilling,
  openNewUrlPath,
  showAlertForUpgradeAccountOnAppleAndAndroid,
  trackEvent,
} from "../backend/utils/helper-methods";
import {
  askForNotificationPermissionIfItIsTime,
  NotificationPermissionAskMoment,
} from "../backend/utils/notification-permission-asks";
import { detectUserCountry } from "../backend/utils/locationUtils";
import { getFirebaseAuthorizationHeaders } from "../backend/utils/firebase-auth-headers";
import { getSignedInFirebaseUser } from "../backend/utils/anonymous-read-session";
import {
  loadInstallAttributionBridgeModule,
  loadMetaConversionTrackingModule,
} from "../utils/attribution-module-loaders";
import {
  getMobileStoreProductIdForSubscriptionPlan,
  isMobileSubscriptionUpgrade,
  sendMobileInAppSubscriptionPurchaseRequest,
} from "../components/upsell/mobileSubscriptionUpgradeHelpers";
import {
  getSubscriptionPurchaseRoute,
  SubscriptionPurchaseRoute,
} from "../components/upsell/subscriptionPurchaseRoute";
import { YEARLY_CHECKOUT_GUARANTEE_VARIANT } from "../components/upsell/guaranteeExperiment";
import { useEffect, useRef } from "react";
import { getUrlForFileUploadToS3, readFile, uploadFileToS3 } from "../pages/home-page/helperMethodsForHomePage";
import { reportDocumentUploadFailure } from "../pages/home-page/document-upload-sentry";
import {
  FileUploadError,
  FileUploadErrorCode,
  FileUploadInputMethod,
  FileUploadStage,
  FileUploadTranslate,
  areAnyFilesBeingRead,
  createUploadAttemptId,
  getUnsupportedFileUploadMessage,
  getUnsupportedUploadFileType,
  getFileUploadBatchRecoveryAvailability,
  markFileUploadBatchAsInactive,
  markFileUploadAsFailed,
  validateSelectedFileCount,
} from "../pages/home-page/file-upload-client";
import { createRoot } from "react-dom/client";
import ImageAndLabelBoxesForOcclusionImage from "../components/ImageAndLabelBoxesForOcclusionImage";
import { auth, db, logFirebaseAnalyticsEvent } from "../backend/utils/init-firebase";
import { buildCardsAfterFirstReview } from "../first-experience-tests/cardsAfterFirstReview";
import {
  isTheFirstReviewOnScreenTheAccountsFirstReview,
  noteAnswerFirstReviewEnded,
} from "../pages/review-deck-page/first-user-experience/answer-first/answerFirstReview";
import { updateUser } from "../backend/models/UserModel";
import { doc, onSnapshot } from "firebase/firestore";
import { addSentryDiagnosticBreadcrumb, trackError } from "../utils/analytics/track-error";
import {
  ContentIngestionError,
  ContentIngestionErrorCode,
  ContentIngestionStage,
  getContentIngestionUserMessage,
  normalizeContentIngestionError,
  translateContentIngestionUserMessage,
} from "../backend/utils/content-ingestion-observability";
import { reportContentUrlIngestionFailure } from "../backend/utils/content-ingestion-sentry";
import {
  MobileAuthenticationError,
  MOBILE_AUTHENTICATION_BRIDGE_VERSION,
  authenticateWebViewFromMobileMessage,
  getMobileAuthenticationSessionIdentifier,
  signOutWebViewAfterMobileAuthenticationEnded,
} from "../backend/utils/mobile-authentication";
import type { MobileAuthenticationResult } from "../backend/utils/mobile-authentication";
import {
  getNativeClientBuildInfoForCurrentAuthentication,
  parseNativeClientBuildInfoMessage,
} from "../utils/analytics/client-build-info";
import { applyNativeClientBuildInfoToTelemetry } from "../utils/analytics/telemetry-build-context";
import { MobileAuthenticationSignOutCoordinator } from "../backend/utils/mobile-authentication-sign-out-grace";
import {
  beginMobileAuthenticationBootstrapAttempt,
  completeMobileAuthenticationBootstrapAttempt,
  getMobileAuthenticationBootstrapState,
  isAuthoritativeMobileAuthenticationSignedOutReason,
  markMobileAuthenticationBootstrapSignedOut,
  markMobileAuthenticationBootstrapWebViewOnlySignedIn,
  MobileAuthenticationSignedOutReason,
  shouldClearAuthenticationFromTimedOutMobileBootstrapAttempt,
  shouldPreserveAuthenticatedWebViewAfterNativeSignOut,
  startMobileAuthenticationBootstrapHandshake,
} from "../backend/utils/mobile-authentication-bootstrap";
import { endSidePanelVoiceCallForQuestionNavigation } from "../components/card-navigator-component/voiceDiscoveryGuard";
import { maybeOfferClinicalExamStationInReview } from "../clinical-exams/offerClinicalExamStationInReview";
import { handleMobilePushNotificationOpenedMessage } from "../backend/utils/push-notification-opened";

const reportedMobileAuthenticationFailures = new Set<string>();
const trackedMobileAuthenticationCompletions = new Set<string>();
const mobileAuthenticationSignOutCoordinator = new MobileAuthenticationSignOutCoordinator();
// One bridge event reaches every mounted copy of this hook (each adds its own listener), so a push tap message is
// confirmed and handled by the first of them only: the event's detail object is the same for all.
const pushNotificationOpenedBridgeEventsAlreadyHandled = new WeakSet<object>();

export const CHECKOUT_AUTHORIZATION_TIMEOUT_MS = 3000;

/**
 * The signed-in user's Firebase ID token for the Stripe checkout request, which lets the Worker store the
 * checkout's Meta context at once. Optional: without it (no user, token error, or no token within
 * CHECKOUT_AUTHORIZATION_TIMEOUT_MS) the request is sent as before and the Worker's slower fallback applies.
 */
async function getCheckoutAuthorizationHeadersIfAvailable(): Promise<Record<string, string>> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      getFirebaseAuthorizationHeaders(),
      new Promise<Record<string, string>>((resolve) => {
        timeoutId = setTimeout(() => resolve({}), CHECKOUT_AUTHORIZATION_TIMEOUT_MS);
      }),
    ]);
  } catch {
    return {};
  } finally {
    clearTimeout(timeoutId);
  }
}

export interface FileUploadBatchControls {
  cancel: () => void;
  retry: () => void;
  canCancel: boolean;
  canRetry: boolean;
}

export type FileUploadBatchControlsChange = (controls: FileUploadBatchControls | null) => void;

export const MOBILE_SUBSCRIPTION_CHECKOUT_STARTED_EVENT = "Mobile Subscription Checkout Started";

/**
 * Starts an App Store / Google Play checkout and records that it started. The Worker records a web checkout's
 * start ("Subscription Checkout Session Created"); a store checkout never reaches that code, so without this
 * event nobody can count the store checkouts that were started and not finished.
 */
async function startMobileStoreCheckout(
  purchaseRequest: Parameters<typeof sendMobileInAppSubscriptionPurchaseRequest>[0]
): Promise<void> {
  if (purchaseRequest.isRequestCurrent && !purchaseRequest.isRequestCurrent()) return;
  // No store product for this plan means the store sheet cannot open: that is not a started checkout.
  const storeProductId = getMobileStoreProductIdForSubscriptionPlan(
    purchaseRequest.subscriptionPricingDetails,
    purchaseRequest.subscriptionPlanToUpgradeTo,
    purchaseRequest.subscriptionPeriodType
  );
  if (storeProductId) {
    void trackEvent(
      MOBILE_SUBSCRIPTION_CHECKOUT_STARTED_EVENT,
      {
        store: purchaseRequest.isAndroid ? "google_play" : "app_store",
        subscriptionPlan: purchaseRequest.subscriptionPlanToUpgradeTo,
        subscriptionFrequency: purchaseRequest.subscriptionPeriodType,
        isSubscriptionUpgrade: purchaseRequest.isSubscriptionUpgrade === true,
      },
      purchaseRequest.user,
      null
    );
  }
  await sendMobileInAppSubscriptionPurchaseRequest(purchaseRequest);
}

export const useHelperMethodsHook = () => {
  const billingLifetime = useRef({ mounted: true, generation: 0 });
  useEffect(() => {
    billingLifetime.current.mounted = true;
    return () => {
      billingLifetime.current.mounted = false;
      billingLifetime.current.generation += 1;
    };
  }, []);
  const createBillingAuthority = (requestUser: UserData, isCallerCurrent: () => boolean = () => true) => {
    const generation = billingLifetime.current.generation;
    return createSubscriptionRequestAuthority(requestUser, () => billingLifetime.current.mounted &&
      billingLifetime.current.generation === generation && isCallerCurrent());
  };
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  //zustand states
  const {
    indexOfCurrentCard,
    cardsData,
    isUserInMiniReviewMode,
    setShowGenerateMoreQuestionsUpsellPage,
    cardsViewedWithoutSession,
    setIndexOfCurrentCard,
    showCheckpointWithPerformanceOverviewPage,
    setShowCheckpointWithPerformanceOverviewPage,
    showShareQuestionsInReviewCheckpointPage,
    setShowShareQuestionsInReviewCheckpointPage,
    hasShownShareQuestionsInReviewCheckpointPageInCurrentSession,
    setHasShownShareQuestionsInReviewCheckpointPageInCurrentSession,
    understandingRatingsStateForNonAuthenticatedUser,
    showGenerateMoreQuestionsUpsellPage,
    setShowReviewSessionCompletedWithJungleRiverScreen,
    setShowEarnedBadgesReviewSessionCompletedScreen,
    shouldShowRetryMissedQuestionsReview,
    indexOfCurrentMissedQuestionCard,
    setIndexOfCurrentMissedQuestionCard,
    cardsDataForReviewingMistakes,
    setShouldShowRetryMissedQuestionsReview,
    setLoadingPercentage,
    showCardEditingPage,
    showDeckPerformanceOverviewPage,
    setShouldShowCheckpointWithPerformanceOverivewWithSkippedCard,
    setShowDeckPerformanceOverviewPage,
    shouldShowCheckpointWithPerformanceOverivewWithSkippedCard,
    shouldShowSkippedCard,
    setCardsData,
    setShowLeaderboardLeagueResultsPage,
    showLeaderboardLeagueResultsPage,
    allCardsForCurrentDeckAndSubdecks,
    setShouldShowSkippedCard,
    setShouldShowMCQOrTrueOrFalseQuestionAnswerSideNavigator,
    setIsExplanationForUserAnswerVisible,
  } = cardStore((state) => state);
  const { setShouldShowStreakIncreasedPage } = useStreakStore((state) => state);
  const { setShowAddYourExamAfterMiniReviewPage } = navigationWithStatesStore((state) => state);
  const { setShowModalSignUpAndLogin } = signUpAndOnboardingModalStore((state) => state);
  const { setShouldShowPDFViewer } = usePDFViewerStore((state) => state);
  const {
    shouldShowEditOcclusionBoxesView,
    setShouldShowEditOcclusionBoxesView,
    showReferralMessageInCardsExperience,
    setShowReferralMessageInCardsExperience,
  } = useGeneratedCardsStore((state) => state);
  const {
    currentReviewDeck,
    setCurrentReviewDeck,
    setDocumentsAndUrlsForCardGenerationArray,
    setCurrentInputIndexInDocumentsAndUrlsArray,
  } = useReviewDeckStore((state) => state);
  const {
    selectedOcclusionImagesWithBoxPositions,
    setSelectedOcclusionImagesWithBoxPositions,
    setShouldShowSelectImagesForOcclusionView,
    setCurrentContentMediumType,
  } = generateCardsStore((state) => state);
  const {
    isRunningAsMobileApp,
    setIsRunningAsMobileApp,
    isAndroid,
    isIOS,
    setIsAndroid,
    setIsIOS,
    currentCountryUserIsIn,
    setCurrentCountryUserIsIn,
    setGooglePlaySubscriptionPriceDetails,
    setAppleSubscriptionPriceDetails,
    setIosVersionPublished,
    setAndroidVersionPublished,
  } = pageStore((state) => state);

  const showAllQuestionsListView = searchParams.get("showListView");

  // redux states
  const user = userStore((state) => state.currentUser);
  const areFlashcardsBeingGenerated = legacyReduxStore((state) => state.areFlashcardsBeingGenerated);

  //variables
  const isUserDoingDailyReview = location.pathname.includes("review-decks");
  let numOfQuestions = cardsData ? cardsData?.length : 0;
  const { userSubscriptionPlan } = getUserSubscriptionPlanAndLimits(user);
  let isThereAnOcclusionImageThatNeedsToBeEdited = selectedOcclusionImagesWithBoxPositions
    ? selectedOcclusionImagesWithBoxPositions.some((imageObject) => !imageObject.hasBeenEditedByUser)
    : false;
  let isUserOnLastCard = indexOfCurrentCard + 1 === numOfQuestions;
  let numOfCardsPerCheckpoint =
    (user?.number_of_sets_of_cards_generated ? user?.number_of_sets_of_cards_generated : 0) <= 1 &&
    indexOfCurrentCard < 10
      ? 5
      : 10;
  // here we check for a special case where if there is at least 8 cards and less than 20 cards, we want to show a checkpoint at the midway point of the number of cards
  if (numOfCardsPerCheckpoint === 10 && numOfQuestions >= 8 && numOfQuestions < 20) {
    numOfCardsPerCheckpoint = Math.floor(numOfQuestions / 2);
  }
  const globalWindow = window as any;
  const toltReferralId = globalWindow?.tolt_referral;

  useEffect(() => {
    const handleMessageFromJungleMobileApp = (jungleAppEvent: any) => {
      listenForEventsFromMobileApp(jungleAppEvent?.detail);
    };
    window.addEventListener("messageFromJungleMobileApp", handleMessageFromJungleMobileApp);
    const stopMobileAuthenticationBootstrapHandshake = startMobileAuthenticationBootstrapHandshake(() => {
      sendEventsToMobileApp(EventSentToMobileApp.INIT_APP);
    });

    return () => {
      stopMobileAuthenticationBootstrapHandshake();
      window.removeEventListener("messageFromJungleMobileApp", handleMessageFromJungleMobileApp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- The bridge listener is owned for this hook's mounted lifetime.
  }, []);

  // methods
  const getNumberOfUnderstandingCardsForNonAuthenticatedUser = () => {
    let understandingRatingsForNonAuthenticatedUser: UnderstandingRatingsForNonAuthenticatedUser =
      understandingRatingsStateForNonAuthenticatedUser
        ? understandingRatingsStateForNonAuthenticatedUser
        : localStorage.getItem("understandingRatingsStateForNonAuthenticatedUser")
          ? JSON.parse(localStorage.getItem("understandingRatingsStateForNonAuthenticatedUser"))
          : {};
    const numberOfUnderstandingCardsForNonAuthenticatedUser: number = Object.values(
      understandingRatingsForNonAuthenticatedUser
    ).reduce((totalSize, currentCard) => totalSize + Object.keys(currentCard).length, 0);
    return numberOfUnderstandingCardsForNonAuthenticatedUser;
  };

  const handleAfterReviewCompletedFlow = async () => {
    if (
      user?.gamification_properties?.leaderboard_leagues_metadata?.current_group_id &&
      !showLeaderboardLeagueResultsPage
    ) {
      setShowLeaderboardLeagueResultsPage(true);
      return;
    }
    if (
      user?.gamification_properties?.badges_progress_and_stats?.ids_of_earned_badges_user_has_not_seen &&
      user?.gamification_properties?.badges_progress_and_stats?.ids_of_earned_badges_user_has_not_seen.length > 0
    ) {
      setShowEarnedBadgesReviewSessionCompletedScreen(true);
      return;
    }
    setShowReviewSessionCompletedWithJungleRiverScreen(true);
    return;
  };

  // Opens the ~1-minute voice quiz on a concept the learner has already
  // covered in this review. Called when "next" is pressed after an answer took
  // the learner's progress across the voice check's mark on the progress bar
  // (reviewProgressMarkRules.ts), which happens at most once per review
  // session. Reads the cards via cardStore.getState() so it can't run on stale
  // closure values across right-arrow presses.
  const openReviewVoiceCheck = (): boolean => {
    const cardStoreState = cardStore.getState();

    // No affordability gate: the check opens regardless of remaining voice
    // minutes. A non-Elite learner who's out (or runs out mid-call) is routed
    // into the upgrade modal by the check page itself — so it doubles as a
    // free→paid conversion moment instead of being silently withheld.

    const coveredConceptSelection = selectRandomCoveredConceptSource(
      cardStoreState.cardsData,
      cardStoreState.indexOfCurrentCard
    );
    if (!coveredConceptSelection) return false;
    const coveredPassageChallengeContext = buildCoveredPassageChallengeContext(
      cardStoreState.cardsData,
      cardStoreState.indexOfCurrentCard,
      coveredConceptSelection.sourceCardIndex
    );

    // Speak/quiz in the deck's language (not hardcoded English) so non-English
    // decks get a localized opener. Same resolver the side-panel voice uses:
    // current card's generation language → deck's page-text language → browser
    // → "English". All cards in a deck share a language, so the current card is
    // a fine proxy for the (possibly earlier) card the concept came from.
    const currentCardForLanguage = cardStoreState.cardsData?.[cardStoreState.indexOfCurrentCard];
    const checkpointLanguage = resolveVoiceOpeningReplyLanguage({
      cardGenerationLanguage: currentCardForLanguage?.card_generation_language,
      deckPageTextLanguage: currentReviewDeck?.generation_metadata?.page_text_language,
    });

    useReviewCheckpointChallengeStore.getState().openReviewCheckpointChallenge({
      concept: coveredConceptSelection.concept,
      deckId: currentReviewDeck?.id,
      deckName: currentReviewDeck?.name,
      language: checkpointLanguage,
      passageContext: coveredPassageChallengeContext.passageContext,
      exploredPassageQuestions: coveredPassageChallengeContext.exploredQuestions,
    });
    return true;
  };

  // The first-experience test T2b: on its "voice_check" side, the first review ends with the voice check
  // (firstExperienceGelRules.ts). Called on the press after the first review's last answer, instead of opening
  // the "great work" page; the page (and with it the paywall) opens when the check closes, however it closes.
  // Returns false when there is no check to open, and the page opens at once as before.
  const openVoiceCheckBeforeFirstReviewGreatWorkPage = (): boolean => {
    const reviewProgressMarksStore = useReviewProgressMarksStore.getState();
    const firstReviewEndsWithVoiceCheck = doesFirstReviewEndWithVoiceCheck({
      isSignedIn: Boolean(user?.id),
      isOnTheVoiceCheckSide: getFirstExperienceTestVariant(FIRST_REVIEW_VOICE_CHECK_TEST, user) === "voice_check",
      numberOfFirstReviewQuestions: cardStore.getState().cardsData?.length ?? 0,
    });
    // Once per review session: the learner may come back to this press (the back button, then the last answer)
    if (
      !firstReviewEndsWithVoiceCheck ||
      reviewProgressMarksStore.markKindsCrossedInThisReviewSession.includes("voice_check")
    ) {
      return false;
    }
    // When the voice check or the station closes: on to the first review's streak page, and the pages after it
    // (pagesAfterTheFirstReview.ts). Only if the learner is still at the end of the first review (both cover the
    // whole screen, but the browser's back button can still leave the review underneath).
    const goOnToThePagesAfterTheFirstReview = () => {
      const cardStoreState = cardStore.getState();
      const isStillAtTheEndOfTheFirstReview =
        cardStoreState.isUserInMiniReviewMode &&
        !cardStoreState.showCheckpointWithPerformanceOverviewPage &&
        cardStoreState.indexOfCurrentCard === (cardStoreState.cardsData?.length ?? 0) - 1;
      if (isStillAtTheEndOfTheFirstReview && !openTheNextPageAfterTheFirstReview()) {
        cardStoreState.setShowCheckpointWithPerformanceOverviewPage(true);
      }
    };

    // T2c: a health student on its "osce_station" side gets the first set's station instead, when it has one due
    // (firstExperienceGelRules.ts). The review page has already worked out which sets have one
    // (useClinicalExamStationsDueInReview), so this only looks the answer up.
    const stationStoreState = useClinicalExamStationStore.getState();
    const firstSetId = currentReviewDeck?.id;
    const stationDueForTheFirstSet = firstSetId ? stationStoreState.stationsDueInReviewByDeckId[firstSetId] : undefined;
    const endsWithStation = doesFirstReviewEndWithClinicalExamStation({
      isInTheTest: isInTheFirstReviewStationTest({
        endsWithVoiceCheck: firstReviewEndsWithVoiceCheck,
        isHealthStudent: isHealthRelatedStudentType(user?.student_type),
        isClinicalExamPracticeEnabled: isClinicalExamPracticeEnabledForUser(user),
      }),
      isOnTheStationSide: getFirstExperienceTestVariant(FIRST_REVIEW_OSCE_STATION_TEST, user) === "osce_station",
      hasStationDueForTheFirstSet: Boolean(stationDueForTheFirstSet) && stationStoreState.openStation === null,
    });
    if (endsWithStation && firstSetId && stationDueForTheFirstSet) {
      stationStoreState.markStationOfferedInThisReviewSession();
      stationStoreState.openClinicalExamStation({
        clinicalExamId: stationDueForTheFirstSet.clinicalExamId,
        deck: { id: firstSetId, name: stationDueForTheFirstSet.deckName },
        entryPoint: "first_review",
      });
      void trackEvent(
        "clinical exam station offered in review",
        {
          exam_id: stationDueForTheFirstSet.clinicalExamId,
          deck_id: firstSetId,
          offer_kind: stationDueForTheFirstSet.offerKind,
          last_score_percent: stationDueForTheFirstSet.lastScorePercent,
          entry_point: "first_review",
        },
        undefined,
        null
      );
      // The set's own station mark does not open it again in this session. The station took the voice check's place,
      // so that slot is used too: back to the last answer and "continue" again goes straight on, and the rest of the
      // set's voice check mark stays off, as after the voice check itself
      reviewProgressMarksStore.noteMarkKindOpenedWithoutItsMark("clinical_exam_station");
      reviewProgressMarksStore.noteMarkKindOpenedWithoutItsMark("voice_check");
      const stopWaitingForTheStationToClose = useClinicalExamStationStore.subscribe((stationState) => {
        if (stationState.openStation) return;
        stopWaitingForTheStationToClose();
        goOnToThePagesAfterTheFirstReview();
      });
      return true;
    }

    if (!openReviewVoiceCheck()) return false;
    // The rest of the set's own voice check mark does not open it again in this session
    reviewProgressMarksStore.noteMarkKindOpenedWithoutItsMark("voice_check");

    const stopWaitingForTheVoiceCheckToClose = useReviewCheckpointChallengeStore.subscribe((challengeState) => {
      if (challengeState.reviewCheckpointChallenge) return;
      stopWaitingForTheVoiceCheckToClose();
      goOnToThePagesAfterTheFirstReview();
    });
    return true;
  };

  const rightArrowKeyAction = () => {
    window.scrollTo({ top: 0, behavior: "auto" });

    const shouldShowModalSignUpAndLogin = signUpAndOnboardingModalStore.getState().shouldShowModalSignUpAndLogin;
    const showCheckpointWithPerformanceOverviewPage = cardStore.getState().showCheckpointWithPerformanceOverviewPage;
    const indexOfCurrentCard = cardStore.getState().indexOfCurrentCard;
    const isUserOnTheLastCardOfTheDeck = indexOfCurrentCard === numOfQuestions - 1;
    const isUserInMiniReviewMode = cardStore.getState().isUserInMiniReviewMode;

    setShouldShowMCQOrTrueOrFalseQuestionAnswerSideNavigator(false);
    setIsExplanationForUserAnswerVisible(false);
    endSidePanelVoiceCallForQuestionNavigation();
    SidePanelOrchestrator.closePanel();

    if (shouldShowModalSignUpAndLogin || showAllQuestionsListView || showCardEditingPage) {
      return;
    }

    // The answer just given took the learner's progress across a mark on the
    // progress bar. They have had the chance to read the answer and ask for an
    // explanation; this press opens what the mark stands for, full screen. The
    // review moves on underneath as for any other press, so they come back to
    // the next question. (The first review has no marks: its voice check, the
    // first-experience test T2b, opens at its end, in
    // handleNavigationForMiniReviewCompletion.)
    const reviewProgressMarkKindToOpen = useReviewProgressMarksStore.getState().takePendingMarkKind();
    if (reviewProgressMarkKindToOpen === "clinical_exam_station") {
      // The station of the set the learner has just been answering from comes
      // first, then the stations of the sets still ahead in this review.
      const allCardsInReview = cardStore.getState().cardsData ?? [];
      maybeOfferClinicalExamStationInReview({
        user,
        cardsReviewedSoFar: [
          ...allCardsInReview.slice(indexOfCurrentCard + 1).reverse(),
          ...allCardsInReview.slice(0, indexOfCurrentCard + 1),
        ],
      });
    } else if (reviewProgressMarkKindToOpen === "voice_check") {
      openReviewVoiceCheck();
    }

    if (useStreakStore.getState().hasUserExtendedTheStreak) {
      setShouldShowStreakIncreasedPage(true);
    }

    // handle navigation for when the user completes the mini review. we show a couple screens after the checkpoint page
    const returnEarly = handleNavigationForMiniReviewCompletion(
      isUserInMiniReviewMode,
      showCheckpointWithPerformanceOverviewPage,
      indexOfCurrentCard,
      isUserOnTheLastCardOfTheDeck
    );
    if (returnEarly) {
      return;
    }

    /*
     * Marks the first free response question as seen during the first card review experience.
     */
    if (user?.number_of_sets_of_cards_generated <= 1 && indexOfCurrentCard > 0) {
      let isFirstFreeResponseQuestion =
        cardsData.findIndex((card) => card?.card_type === CardType.RegularFreeResponseQuestion) === indexOfCurrentCard;
      if (isFirstFreeResponseQuestion) {
        updateUser(user?.id, {
          did_user_see_first_free_response_question: true,
        });
        setCurrentReviewDeck(currentReviewDeck);
      }
    }

    if (!user && getNumberOfUnderstandingCardsForNonAuthenticatedUser() >= 3 && !isUserInMiniReviewMode) {
      setShowModalSignUpAndLogin(
        SignUpAndOnboardingModalView.SignInOrSignUpOptionsView,
        SignUpAndOnboardingModalActivatedReason.GeneratingSecondSetOfCards
      );
      return;
    }

    const updatedShouldShowPDFViewer = usePDFViewerStore.getState().shouldShowPDFViewer;
    if (updatedShouldShowPDFViewer) {
      setShouldShowPDFViewer(false);
    }

    if (isThereAnOcclusionImageThatNeedsToBeEdited) {
      setShouldShowEditOcclusionBoxesView(true);
      return;
    }

    // first, reset these states
    legacyReduxStore.getState().setShouldShowFreeResponseAnswerFeedbackView(false);
    legacyReduxStore.getState().setDidUserSelectIDontKnowOption(false);

    // if we are showing missed questions for users to retry
    if (shouldShowRetryMissedQuestionsReview) {
      if (indexOfCurrentMissedQuestionCard === cardsDataForReviewingMistakes.length - 1) {
        setShowCheckpointWithPerformanceOverviewPage(true);
        setShouldShowRetryMissedQuestionsReview(false);
        //As long as there are missed questions, we want to show the checkpoint screen with the button to retry the missed questions
        const filteredCardsForReviewAllMistakes = filterCardsWithMistakes(cardsData, user);
        if (filteredCardsForReviewAllMistakes?.length > 0) {
          setShouldShowCheckpointWithPerformanceOverivewWithSkippedCard(false);
          setIndexOfCurrentMissedQuestionCard(0);
          return;
        }
        // The first review's retry goes back to its "great work" page too: the rest of the set comes after it
        // ("continue to next questions"), so the session is not over. The end-of-review flow below (the river,
        // the character choice) is for the end of the whole set
        if (isUserInMiniReviewMode) {
          setIndexOfCurrentMissedQuestionCard(0);
          return;
        }

        const skippedCards = cardsData.filter((card) =>
          isUserDoingDailyReview
            ? !card?.understanding_ratings_for_card_by_user?.[user?.id] ||
              card?.understanding_ratings_for_card_by_user?.[user?.id]
                ?.most_recent_spaced_repetition_id_of_knowledge_rating !== user?.current_spaced_repetition_review?.id
            : !card?.understanding_ratings_for_card_by_user?.[user?.id]
        );
        if (skippedCards.length > 0) {
          setShouldShowCheckpointWithPerformanceOverivewWithSkippedCard(true);
          return;
        } else {
          handleAfterReviewCompletedFlow();
          return;
        }
      } else {
        setIndexOfCurrentMissedQuestionCard(indexOfCurrentMissedQuestionCard + 1);
      }
      return;
    }

    if (shouldShowSkippedCard) {
      if (numOfQuestions - 1 > indexOfCurrentCard) {
        // Move focus to next card
        setIndexOfCurrentCard(numOfQuestions - 1 > indexOfCurrentCard ? indexOfCurrentCard + 1 : indexOfCurrentCard);
        return;
      } else {
        // is showing the last card

        handleAfterReviewCompletedFlow();
        setShowCheckpointWithPerformanceOverviewPage(false);
        setCardsData(allCardsForCurrentDeckAndSubdecks);
        setIndexOfCurrentCard(allCardsForCurrentDeckAndSubdecks.length - 1);
        return;
      }
    }
    if (isUserOnLastCard && showCheckpointWithPerformanceOverviewPage && showDeckPerformanceOverviewPage) {
      return;
    }

    if (
      isUserOnLastCard &&
      showCheckpointWithPerformanceOverviewPage &&
      !shouldShowCheckpointWithPerformanceOverivewWithSkippedCard
    ) {
      const skippedCards = cardsData.filter((card) =>
        isUserDoingDailyReview
          ? !card?.understanding_ratings_for_card_by_user?.[user?.id] ||
            card?.understanding_ratings_for_card_by_user?.[user?.id]
              ?.most_recent_spaced_repetition_id_of_knowledge_rating !== user?.current_spaced_repetition_review?.id
          : !card?.understanding_ratings_for_card_by_user?.[user?.id]
      );
      if (skippedCards.length > 0) {
        setShouldShowCheckpointWithPerformanceOverivewWithSkippedCard(true);
        return;
      } else {
        handleAfterReviewCompletedFlow();
        setShowCheckpointWithPerformanceOverviewPage(false);
        setShouldShowCheckpointWithPerformanceOverivewWithSkippedCard(false);
        return;
      }
    }
    // the button should be disabled if we are on the last card and we are showing the checkpoint with performance overview page
    if (
      isUserOnLastCard &&
      showCheckpointWithPerformanceOverviewPage &&
      shouldShowCheckpointWithPerformanceOverivewWithSkippedCard
    ) {
      setCardsData(allCardsForCurrentDeckAndSubdecks);
      setIndexOfCurrentCard(allCardsForCurrentDeckAndSubdecks.length - 1);

      handleAfterReviewCompletedFlow();
      setShouldShowSkippedCard(false);
      setShowGenerateMoreQuestionsUpsellPage(false);
      setShowDeckPerformanceOverviewPage(false);
      setShowCheckpointWithPerformanceOverviewPage(false);
      setShouldShowCheckpointWithPerformanceOverivewWithSkippedCard(false);
      return;
    }

    // if we're on mini review, skip the completed celebration page
    if (isUserOnLastCard && isUserInMiniReviewMode) {
      setShowCheckpointWithPerformanceOverviewPage(true);
      return;
    }

    // all the different finish screens + logic
    if (isUserOnLastCard && !isUserInMiniReviewMode && !showCheckpointWithPerformanceOverviewPage) {
      // then, we check if we show generate more questions upsell page
      if (!showGenerateMoreQuestionsUpsellPage && userSubscriptionPlan === UserSubscriptionPlan.FREE) {
        setShowGenerateMoreQuestionsUpsellPage(true);
        setShowCheckpointWithPerformanceOverviewPage(false);
        setShouldShowCheckpointWithPerformanceOverivewWithSkippedCard(false);
        return;
      }

      // we check if we show checkpoint with performance overview page
      if (
        (!showCheckpointWithPerformanceOverviewPage && !shouldShowCheckpointWithPerformanceOverivewWithSkippedCard) ||
        (!showCheckpointWithPerformanceOverviewPage && shouldShowCheckpointWithPerformanceOverivewWithSkippedCard)
      ) {
        setShowCheckpointWithPerformanceOverviewPage(true);
        setShowGenerateMoreQuestionsUpsellPage(false);
        setShowDeckPerformanceOverviewPage(false);
        const filteredCardsForReviewAllMistakes = filterCardsWithMistakes(cardsData, user);
        const hasMistakeCards = filteredCardsForReviewAllMistakes?.length > 0;
        setShouldShowCheckpointWithPerformanceOverivewWithSkippedCard(!hasMistakeCards);
        return;
      }

      if (showCheckpointWithPerformanceOverviewPage && !shouldShowCheckpointWithPerformanceOverivewWithSkippedCard) {
        setShowCheckpointWithPerformanceOverviewPage(true);

        setShouldShowCheckpointWithPerformanceOverivewWithSkippedCard(true);
        return;
      }
    }

    const hasUserViewedReferralMessageInCardsExperience = localStorage.getItem(
      "showedReferralMessageInCardsExperience"
    );
    // show referral message component after 3 cards for new users
    if (
      user &&
      !isUserInMiniReviewMode &&
      cardsViewedWithoutSession &&
      cardsViewedWithoutSession.length >= 2 &&
      !examplePageGeneratedIds.includes(currentReviewDeck?.id) &&
      user?.number_of_sets_of_cards_generated <= 1 &&
      !showReferralMessageInCardsExperience &&
      !hasUserViewedReferralMessageInCardsExperience
    ) {
      setShowReferralMessageInCardsExperience(true);
      localStorage.setItem("showedReferralMessageInCardsExperience", "true");
      return;
    }

    if (showCheckpointWithPerformanceOverviewPage) {
      setShowCheckpointWithPerformanceOverviewPage(false);
    }
    if (showShareQuestionsInReviewCheckpointPage) {
      setShowShareQuestionsInReviewCheckpointPage(false);
    }
    if (showReferralMessageInCardsExperience) {
      setShowReferralMessageInCardsExperience(false);
    }

    // logic for if we should show share checkpoint screen before the first checkpoint
    // only show if streak page is not being shown
    if (
      (user?.number_of_sets_of_cards_generated > 1 || user?.did_user_complete_mini_review_on_first_review) &&
      !hasShownShareQuestionsInReviewCheckpointPageInCurrentSession &&
      !showShareQuestionsInReviewCheckpointPage &&
      !useStreakStore.getState().shouldShowStreakIncreasedPage &&
      ((areFlashcardsBeingGenerated && (indexOfCurrentCard + 1) % numOfCardsPerCheckpoint === 0) ||
        (!areFlashcardsBeingGenerated &&
          (indexOfCurrentCard + 1) % numOfCardsPerCheckpoint === 0 &&
          numOfQuestions - indexOfCurrentCard > 5))
    ) {
      setShowShareQuestionsInReviewCheckpointPage(true);
      setHasShownShareQuestionsInReviewCheckpointPageInCurrentSession(true);
      return;
    }

    // logic for if we should show checkpoint screen
    if (
      (user?.number_of_sets_of_cards_generated > 1 || user?.did_user_complete_mini_review_on_first_review) &&
      !showCheckpointWithPerformanceOverviewPage &&
      ((areFlashcardsBeingGenerated && (indexOfCurrentCard + 1) % numOfCardsPerCheckpoint === 0) ||
        (!areFlashcardsBeingGenerated &&
          (indexOfCurrentCard + 1) % numOfCardsPerCheckpoint === 0 &&
          numOfQuestions - indexOfCurrentCard > 5))
    ) {
      setShowCheckpointWithPerformanceOverviewPage(true);
      return;
    }

    if (numOfQuestions - 1 > indexOfCurrentCard) {
      // Move focus to next card
      setIndexOfCurrentCard(numOfQuestions - 1 > indexOfCurrentCard ? indexOfCurrentCard + 1 : indexOfCurrentCard);
    }

    legacyReduxStore.getState().setPreviousQuestionAndAnswerObjectToReview(null);
  };

  const leftArrowKeyAction = async () => {
    window.scrollTo({ top: 0, behavior: "auto" });

    const shouldShowModalSignUpAndLogin = signUpAndOnboardingModalStore.getState().shouldShowModalSignUpAndLogin;
    setShouldShowMCQOrTrueOrFalseQuestionAnswerSideNavigator(false);
    setIsExplanationForUserAnswerVisible(false);
    endSidePanelVoiceCallForQuestionNavigation();
    SidePanelOrchestrator.closePanel();
    if (shouldShowModalSignUpAndLogin || showAllQuestionsListView || showCardEditingPage) {
      return;
    }

    const updatedShouldShowPDFViewer = usePDFViewerStore.getState().shouldShowPDFViewer;
    if (updatedShouldShowPDFViewer) {
      setShouldShowPDFViewer(false);
    }

    let isUserOnLastCard = false;

    if (indexOfCurrentCard + 1 === numOfQuestions) {
      isUserOnLastCard = true;
    }

    legacyReduxStore.getState().setShouldShowFreeResponseAnswerFeedbackView(false);

    // if we are showing missed questions for users to retry
    if (shouldShowRetryMissedQuestionsReview) {
      if (indexOfCurrentMissedQuestionCard > 0) {
        setIndexOfCurrentMissedQuestionCard(indexOfCurrentMissedQuestionCard - 1);
      } else {
        cardStore.setState({
          shouldShowRetryMissedQuestionsReview: false,
          showCheckpointWithPerformanceOverviewPage: true,
        });
      }
      return;
    }

    // on the finish screen
    if (isUserOnLastCard && showCheckpointWithPerformanceOverviewPage) {
      cardStore.setState({
        showCheckpointWithPerformanceOverviewPage: false,
        shouldShowSkippedCard: false,
        showDeckPerformanceOverviewPage: false,
        shouldShowCheckpointWithPerformanceOverivewWithSkippedCard: false,
      });

      return;
    }

    if (!user && getNumberOfUnderstandingCardsForNonAuthenticatedUser() >= 3 && !isUserInMiniReviewMode) {
      setShowModalSignUpAndLogin(
        SignUpAndOnboardingModalView.SignInOrSignUpOptionsView,
        SignUpAndOnboardingModalActivatedReason.GeneratingSecondSetOfCards
      );
      return;
    }
    if (showGenerateMoreQuestionsUpsellPage) {
      setShowGenerateMoreQuestionsUpsellPage(false);
      return;
    }

    if (showShareQuestionsInReviewCheckpointPage) {
      setShowShareQuestionsInReviewCheckpointPage(false);
      return;
    }

    if (showReferralMessageInCardsExperience) {
      setShowReferralMessageInCardsExperience(false);
    }

    // this is for the checkpoint screen
    let numOfCardsPerCheckpoint =
      (user?.number_of_sets_of_cards_generated ? user?.number_of_sets_of_cards_generated : 0) <= 1 &&
      indexOfCurrentCard < 10
        ? 5
        : 10;

    // here we check for a special case where if there is at least 8 cards and less than 20 cards, we want to show a checkpoint at the midway point of the number of cards
    if (numOfCardsPerCheckpoint === 10 && numOfQuestions >= 8 && numOfQuestions < 20) {
      numOfCardsPerCheckpoint = Math.floor(numOfQuestions / 2);
    }
    if (
      // !isUserDoingDailyReview &&
      indexOfCurrentCard !== 0 &&
      !showCheckpointWithPerformanceOverviewPage &&
      indexOfCurrentCard % numOfCardsPerCheckpoint === 0 &&
      numOfQuestions - indexOfCurrentCard > 8 &&
      (user?.number_of_sets_of_cards_generated > 1 ||
        indexOfCurrentCard > 10 ||
        user?.did_user_complete_mini_review_on_first_review)
    ) {
      setShowCheckpointWithPerformanceOverviewPage(true);
    }

    legacyReduxStore.getState().setDidUserSelectIDontKnowOption(false);
    if (shouldShowEditOcclusionBoxesView) {
      setShouldShowEditOcclusionBoxesView(false);
    } else if (showCheckpointWithPerformanceOverviewPage) {
      setShowCheckpointWithPerformanceOverviewPage(false);
    } else {
      // we don't want the user to be able to go back from first card if they are in mini review mode or if they just completed mini review mode
      if (indexOfCurrentCard === 0) {
        if (isUserInMiniReviewMode === null) {
          // if the user is in mini review mode, we don't want to go back to the previous page
          // window.history.back();
          return;
        }
      } else {
        setIndexOfCurrentCard(indexOfCurrentCard > 0 ? indexOfCurrentCard - 1 : 0);
      }
    }

    legacyReduxStore.getState().setPreviousQuestionAndAnswerObjectToReview(null);
  };

  function handleNavigationForMiniReviewCompletion(
    isUserInMiniReviewMode: boolean,
    showCheckpointWithPerformanceOverviewPage: boolean,
    indexOfCurrentCard: number,
    isUserOnTheLastCardOfTheDeck: boolean
  ) {
    const showAddYourExamAfterMiniReviewPage = navigationWithStatesStore.getState().showAddYourExamAfterMiniReviewPage;
    const showDesktopNotificationsOptInScreen =
      navigationWithStatesStore.getState().showDesktopNotificationsOptInScreen;
    const shouldShowRetryMissedQuestionsReview = cardStore.getState().shouldShowRetryMissedQuestionsReview;

    // lets disable rightArrowKey for these pages to force them to make a more concious decision
    if (
      showAddYourExamAfterMiniReviewPage ||
      showDesktopNotificationsOptInScreen ||
      // nor does it skip the first review's streak page, or its opening (pagesAfterTheFirstReview.ts)
      isAPageAfterTheFirstReviewOnScreen()
    ) {
      return true;
    }

    // handle navigation logic for when the user completes the mini review
    if (isUserInMiniReviewMode && !shouldShowRetryMissedQuestionsReview && isUserOnTheLastCardOfTheDeck) {
      // if we are on the last card of the mini review review, we show the checkpoint
      if (
        !showCheckpointWithPerformanceOverviewPage &&
        cardsData?.length !== allCardsForCurrentDeckAndSubdecks?.length
      ) {
        if (openVoiceCheckBeforeFirstReviewGreatWorkPage()) return true;
        // Today's streak day, then the exam question, then the checkpoint (pagesAfterTheFirstReview.ts)
        if (!openTheNextPageAfterTheFirstReview()) setShowCheckpointWithPerformanceOverviewPage(true);
        return true;
      }

      // if we are on the first checkpoint for a mini review review, we mark the mini review as complete
      // setting isUserInMiniReviewMode to false is handled by a useEffect in ReviewQuestionsPage.tsx
      if (showCheckpointWithPerformanceOverviewPage) {
        // A first review fixed before signing up ends here too. For an account that would otherwise have had no first
        // review (one logged in to on the questions screen after a first review long ago), what follows a first
        // review is not shown: the review just goes on, as at the end of any review's checkpoint.
        const isTheAccountsFirstReview = isTheFirstReviewOnScreenTheAccountsFirstReview(currentReviewDeck?.id);
        noteAnswerFirstReviewEnded(currentReviewDeck?.id);

        updateUser(user?.id, {
          did_user_complete_mini_review_on_first_review: true,
        });

        // get all the cards for the deck now that the mini review is complete: the first review's questions as
        // answered, then the rest of the set, so the review carries on at the first question not yet asked
        // however many questions the first review asked (firstReviewComposition.ts)
        const firstReviewCards = cardStore.getState().cardsData ?? [];
        const cardsForFirstReview = buildCardsAfterFirstReview({
          allCards: allCardsForCurrentDeckAndSubdecks ?? [],
          firstReviewCards,
        });

        setCardsData(cardsForFirstReview);
        numOfQuestions = cardsForFirstReview ? cardsForFirstReview?.length : 0;
        setIndexOfCurrentCard(numOfQuestions - 1 > indexOfCurrentCard ? indexOfCurrentCard + 1 : indexOfCurrentCard);

        // handle navigation
        // step 1: we no longer show the checkpoint
        setShowCheckpointWithPerformanceOverviewPage(false);

        if (!isTheAccountsFirstReview) {
          return true;
        }

        // step 2: if the user is a student or teacher, we show the exam (unless it was asked before the checkpoint,
        // as it is whenever the pages after the first review ran: pagesAfterTheFirstReview.ts)
        if (
          (user?.user_type === UserType.Student || user?.user_type === UserType.TeacherOrProfessor) &&
          isTheExamQuestionStillToAskAfterTheCheckpoint()
        ) {
          setShowAddYourExamAfterMiniReviewPage(true);
        } else {
          // otherwise (a learner who is not a student or teacher, or one whose exam question came before
          // the checkpoint and its upsell) we ask for notifications after completing the mini review, when it is
          // time to ask: after the exam date saved before the checkpoint, leading with that exam, if there was one
          const examSavedBeforeTheCheckpoint = takeTheExamSavedBeforeTheFirstReviewsCheckpoint();
          askForNotificationPermissionIfItIsTime(
            examSavedBeforeTheCheckpoint
              ? NotificationPermissionAskMoment.ExamDateSaved
              : NotificationPermissionAskMoment.ReviewFinished,
            examSavedBeforeTheCheckpoint ? { user, savedExam: examSavedBeforeTheCheckpoint } : { user }
          );
        }

        return true;
      }
    }

    return false;
  }

  ///////////////////////////// METHODS FOR GENERATE CARDS /////////////////////////////
  const generateQuestionsForPageUrl = async (
    pageUrl: string,
    setShowModalSignUpAndLogin: any,
    setIsGettingDataFromPageUrl: any,
    setErrorMessage: any,
    setShouldShowChromeExtensionUpsell: any,
    setShouldShowSelectImagesForOcclusionView: any,
    setSelectedOcclusionImagesWithBoxPositions: any,
    shouldAutoGenerateQuestions: boolean,
    setLoadingPercentage?: (percentage: number) => void,
    options: LandingIngestionOptions = {},
    translate: FileUploadTranslate = (_key, fallback) => fallback
  ): Promise<LandingIngestionOutcome> => {
    const haveCardsBeenGeneratedBefore = localStorage.getItem("haveCardsBeenGeneratedBefore");
    if ((!user && !haveCardsBeenGeneratedBefore) || user?.number_of_sets_of_cards_generated === 0) {
      shouldAutoGenerateQuestions = true;
    }

    if (haveCardsBeenGeneratedBefore && !user) {
      setShowModalSignUpAndLogin(
        SignUpAndOnboardingModalView.SignInOrSignUpOptionsView,
        SignUpAndOnboardingModalActivatedReason.GeneratingSecondSetOfCards
      );
      return "authentication-required";
    }

    if (options.restoreToReview) shouldAutoGenerateQuestions = false;

    setIsGettingDataFromPageUrl(true);
    setErrorMessage(null);
    setShouldShowChromeExtensionUpsell(false);
    const ingestionAttemptId = createUploadAttemptId();

    let newGeneratedDeck: DeckObject = {
      is_deck_generated_from_document: true,
      is_active: true,
      cards_count: 0,
      date_created: new Date(),
      generation_metadata: {
        page_url: pageUrl,
        landing_handoff_context: options.context,
      },
    };

    if (setLoadingPercentage) setLoadingPercentage(0);

    try {
      addSentryDiagnosticBreadcrumb({
        category: "content-url-ingestion",
        message: "URL ingestion started",
        data: { attemptId: ingestionAttemptId },
      });
      // This gets all source data needed by the generation screen, including
      // the final medium after redirects or Google-document conversion.
      const { errorMessage, shouldShowChromeExtensionUpsell, sourceRejectionHttpStatus } = await getDataFromUrl(
        newGeneratedDeck,
        user,
        setLoadingPercentage,
        ingestionAttemptId
      );
      if (options.cancellationSignal?.aborted) return "cancelled";
      setShouldShowChromeExtensionUpsell(shouldShowChromeExtensionUpsell);
      if (errorMessage || shouldShowChromeExtensionUpsell) {
        const sourceRejectedError = new ContentIngestionError({
          message: "The source could not be ingested",
          stage: ContentIngestionStage.FetchSource,
          code: ContentIngestionErrorCode.SourceRejected,
          retryable: false,
          httpStatus: sourceRejectionHttpStatus,
          userMessage: errorMessage || undefined,
        });
        reportContentUrlIngestionFailure({
          error: sourceRejectedError,
          attemptId: ingestionAttemptId,
          sourceUrl: pageUrl,
          contentMediumType: newGeneratedDeck.generation_metadata.content_medium_type || null,
          user,
        });
        if (errorMessage) setErrorMessage(translateContentIngestionUserMessage(errorMessage, translate));
        return "failure";
      }
      if (options.onReady && !options.onReady({ sourceUrl: newGeneratedDeck.generation_metadata?.page_url })) return "cancelled";
      legacyReduxStore.getState().setIsRegeneratingQuestions(false);
      addSentryDiagnosticBreadcrumb({
        category: "content-url-ingestion",
        message: "URL ingestion completed",
        data: {
          attemptId: ingestionAttemptId,
          contentMediumType: newGeneratedDeck.generation_metadata.content_medium_type || "unknown",
        },
      });

      const sourceToGeneration: FileUploadWithDeckData[] = [
        {
          file: null,
          associatedDeck: newGeneratedDeck,
          isBeingRead: false,
          hasUploadError: false,
          uploadErrorMessage: null,
          uploadedFileIndex: 0,
        },
      ];
      setCurrentInputIndexInDocumentsAndUrlsArray(0);
      setDocumentsAndUrlsForCardGenerationArray(sourceToGeneration);
      setShouldShowSelectImagesForOcclusionView(false);
      setSelectedOcclusionImagesWithBoxPositions(null);
      navigate(shouldAutoGenerateQuestions ? "/generate-questions/?autoGenerate=true" : "/generate-questions");
      return "success";
    } catch (error) {
      if (options.cancellationSignal?.aborted) return "cancelled";
      const contentIngestionError = normalizeContentIngestionError(error);
      reportContentUrlIngestionFailure({
        error: contentIngestionError,
        attemptId: ingestionAttemptId,
        sourceUrl: pageUrl,
        contentMediumType: newGeneratedDeck.generation_metadata.content_medium_type || null,
        user,
      });
      setErrorMessage(getContentIngestionUserMessage(contentIngestionError, navigator.onLine, translate));
      return "failure";
    } finally {
      if (!options.cancellationSignal?.aborted) setIsGettingDataFromPageUrl(false);
    }
  };

  const sendEventsToMobileApp = (typeEvent: EventSentToMobileApp, message?: string) => {
    const postEvent = () => {
      try {
        window.ReactNativeWebView?.postMessage(serializeMobileAppEvent(typeEvent, message));
      } catch (error) {
        trackError(error, {
          scope: "mobile-app-bridge",
          userId: user?.id,
          tags: { event_type: typeEvent },
        });
      }
    };
    if (typeEvent === EventSentToMobileApp.LOADED_PAGE) {
      revealInitialHomeWhenSettled(postEvent);
    } else {
      postEvent();
    }
  };

  const reportMobileAuthenticationFailure = ({
    error,
    code,
    bridgeVersion,
    userId,
    nativeAppVersion,
    nativeBuildNumber,
    responseStatus,
    firebaseErrorCode,
    initialFirebaseErrorCode,
    firebaseRetryAttempted,
    firebaseRetryFailureStage,
  }: {
    error: unknown;
    code: string;
    bridgeVersion: string;
    userId?: string;
    nativeAppVersion?: string;
    nativeBuildNumber?: string;
    responseStatus?: number;
    firebaseErrorCode?: string;
    initialFirebaseErrorCode?: string;
    firebaseRetryAttempted?: boolean;
    firebaseRetryFailureStage?: string;
  }) => {
    const failureFingerprint = [
      code,
      bridgeVersion,
      nativeAppVersion ?? "unknown",
      userId ?? "unknown",
      firebaseErrorCode ?? "not_applicable",
      firebaseRetryFailureStage ?? "not_applicable",
    ].join(":");
    if (reportedMobileAuthenticationFailures.has(failureFingerprint)) return;
    reportedMobileAuthenticationFailures.add(failureFingerprint);

    trackError(error, {
      scope: "mobile-authentication-bridge",
      fingerprint: ["mobile-authentication-bridge", code, firebaseErrorCode ?? "not_applicable"],
      userId,
      level: code === "legacy_bridge_rejected" ? "warning" : "error",
      tags: {
        feature: "mobile_authentication",
        outcome: code,
        bridge_version: bridgeVersion,
        firebase_error_code: firebaseErrorCode ?? "not_applicable",
        firebase_retry_attempted: String(firebaseRetryAttempted === true),
        firebase_retry_failure_stage: firebaseRetryFailureStage ?? "not_applicable",
      },
      extra: {
        nativeAppVersion: nativeAppVersion ?? null,
        nativeBuildNumber: nativeBuildNumber ?? null,
        responseStatus: responseStatus ?? null,
        initialFirebaseErrorCode: initialFirebaseErrorCode ?? null,
      },
    });
  };

  const performMobileAuthenticationSignOut = async (allowPreservingInitialWebViewSession = false) => {
    const isCurrentAuthentication = mobileAuthenticationSignOutCoordinator.captureCurrentAuthentication();
    // Email/password authentication is completed inside the WebView, so older
    // native builds can legitimately have no parallel native Firebase user.
    // A still-valid WebView Firebase session is authoritative in that case.
    // Explicit logout signs the WebView out before notifying native, so it
    // continues through the sign-out path below.
    const shouldPreserveInitialWebViewOnlySession =
      allowPreservingInitialWebViewSession &&
      shouldPreserveAuthenticatedWebViewAfterNativeSignOut(
        getSignedInFirebaseUser()?.uid,
        getMobileAuthenticationBootstrapState()
      );
    if (shouldPreserveInitialWebViewOnlySession) {
      markMobileAuthenticationBootstrapWebViewOnlySignedIn();
      return;
    }

    try {
      await signOutWebViewAfterMobileAuthenticationEnded();
      void trackEvent("Mobile Authentication Bridge Signed Out", {}, null, null);
    } catch (error) {
      const mobileAuthenticationError = error instanceof MobileAuthenticationError ? error : null;
      reportMobileAuthenticationFailure({
        error,
        code: mobileAuthenticationError?.code ?? "unexpected_sign_out_error",
        bridgeVersion: mobileAuthenticationError?.bridgeVersion ?? String(MOBILE_AUTHENTICATION_BRIDGE_VERSION),
      });
    } finally {
      // Native confirmation may already have begun a replacement handoff
      // while the serialized Firebase sign-out was completing.
      if (isCurrentAuthentication()) markMobileAuthenticationBootstrapSignedOut();
    }
  };

  const performAuthoritativeMobileAuthenticationSignOut = () => performMobileAuthenticationSignOut(false);
  const performProvisionalNativeEmptyStateSignOut = () => performMobileAuthenticationSignOut(true);

  const handleMobileAuthenticationMessage = async (rawMessage: unknown) => {
    // An old INIT_APP response can arrive after another account's token. A
    // session already rejected by containment must not supersede that handoff
    // or put its bootstrap back into pending, even after an ID-token refresh.
    if (mobileAuthenticationSignOutCoordinator.isRejectedNativeSession(
      getMobileAuthenticationSessionIdentifier(rawMessage)
    )) return;
    // A native token supersedes both queued and subsequently delivered initial
    // empty-state responses, including while the token exchange is in flight.
    mobileAuthenticationSignOutCoordinator.noteNativeAuthenticationReceived();
    const mobileAuthenticationBootstrapAttempt = beginMobileAuthenticationBootstrapAttempt();

    try {
      const authenticationResult: MobileAuthenticationResult = await authenticateWebViewFromMobileMessage(
        rawMessage,
        (userId, sessionIdentifier) => mobileAuthenticationSignOutCoordinator.beginNativeReauthentication(
          userId,
          sessionIdentifier,
          () => sendEventsToMobileApp(EventSentToMobileApp.LOGOUT)
        ),
        (sessionIdentifier) => mobileAuthenticationSignOutCoordinator.noteRejectedNativeSession(sessionIdentifier)
      );

      if (authenticationResult.status === "reauthentication_required") {
        markMobileAuthenticationBootstrapSignedOut();
        return;
      }

      if (authenticationResult.status === "legacy_bridge_rejected") {
        await mobileAuthenticationSignOutCoordinator.signOutImmediately(
          performAuthoritativeMobileAuthenticationSignOut
        );
        reportMobileAuthenticationFailure({
          error: new Error("The installed mobile app cannot securely authenticate this WebView"),
          code: authenticationResult.status,
          bridgeVersion: authenticationResult.bridgeVersion,
          userId: authenticationResult.userId,
        });
        return;
      }

      const nativeClientBuildInfo = getNativeClientBuildInfoForCurrentAuthentication(authenticationResult);
      if (nativeClientBuildInfo) {
        applyNativeClientBuildInfoToTelemetry(nativeClientBuildInfo);
      }

      if (authenticationResult.status === "signed_in" || authenticationResult.status === "already_signed_in") {
        // A legacy logout reply may have scheduled its grace timer while this
        // handoff was exchanging. A confirmed replacement cancels that timer.
        mobileAuthenticationSignOutCoordinator.cancelPendingSignOut();
        const didCompleteCurrentMobileAuthenticationAttempt = completeMobileAuthenticationBootstrapAttempt(
          mobileAuthenticationBootstrapAttempt
        );
        if (!didCompleteCurrentMobileAuthenticationAttempt) {
          const shouldClearAuthenticationCompletedAfterTimeout =
            shouldClearAuthenticationFromTimedOutMobileBootstrapAttempt(mobileAuthenticationBootstrapAttempt);
          if (shouldClearAuthenticationCompletedAfterTimeout) {
            await signOutWebViewAfterMobileAuthenticationEnded();
          }
          return;
        }
      }

      if (authenticationResult.status === "signed_in") {
        const completionFingerprint = [
          authenticationResult.bridgeVersion,
          authenticationResult.userId,
          authenticationResult.nativeAppVersion ?? "unknown",
          authenticationResult.nativeBuildNumber ?? "unknown",
        ].join(":");
        if (trackedMobileAuthenticationCompletions.has(completionFingerprint)) return;
        trackedMobileAuthenticationCompletions.add(completionFingerprint);

        void trackEvent(
          "Mobile Authentication Bridge Completed",
          {
            bridgeVersion: authenticationResult.bridgeVersion,
            nativeAppVersion: authenticationResult.nativeAppVersion ?? null,
            nativeBuildNumber: authenticationResult.nativeBuildNumber ?? null,
            firebaseRetryAttempted: authenticationResult.firebaseRetryAttempted === true,
            initialFirebaseErrorCode: authenticationResult.initialFirebaseErrorCode ?? null,
          },
          null,
          null
        );
      }
    } catch (error) {
      await mobileAuthenticationSignOutCoordinator.signOutImmediately(performAuthoritativeMobileAuthenticationSignOut);
      const mobileAuthenticationError = error instanceof MobileAuthenticationError ? error : null;
      reportMobileAuthenticationFailure({
        error,
        code: mobileAuthenticationError?.code ?? "unexpected_error",
        bridgeVersion: mobileAuthenticationError?.bridgeVersion ?? "unknown",
        userId: mobileAuthenticationError?.userId,
        nativeAppVersion: mobileAuthenticationError?.nativeAppVersion,
        nativeBuildNumber: mobileAuthenticationError?.nativeBuildNumber,
        responseStatus: mobileAuthenticationError?.responseStatus,
        firebaseErrorCode: mobileAuthenticationError?.firebaseErrorCode,
        initialFirebaseErrorCode: mobileAuthenticationError?.initialFirebaseErrorCode,
        firebaseRetryAttempted: mobileAuthenticationError?.firebaseRetryAttempted,
        firebaseRetryFailureStage: mobileAuthenticationError?.firebaseRetryFailureStage,
      });
    }
  };

  const handleMobileAuthenticationSignedOut = (reason: unknown) => {
    const confirmNativeState = () => mobileAuthenticationSignOutCoordinator.confirmNativeStateAfterSignOut(
      () => sendEventsToMobileApp(EventSentToMobileApp.INIT_APP)
    );
    if (reason === MobileAuthenticationSignedOutReason.INITIAL_NATIVE_STATE_EMPTY) {
      // Modern native clients send this only after Firebase resolves its initial
      // state. Wait for web persistence too (email logins may exist only here),
      // then resolve immediately instead of adding a fixed three-second splash.
      void mobileAuthenticationSignOutCoordinator
        .requestSignOutWhenReady(() => auth.authStateReady(), performProvisionalNativeEmptyStateSignOut)
        .catch((error) => {
          trackError(error, {
            scope: "mobile-initial-authentication-readiness",
            tags: { feature: "mobile_authentication" },
          });
        });
      confirmNativeState();
      return;
    }
    // Released clients do not include a reason. Preserve their independent
    // WebView email session just as before; explicit web logout has already
    // cleared auth.currentUser before native replies. New clients distinguish
    // initial native-empty bootstrap responses from real auth termination.
    const isAuthoritativeNativeSignOut = isAuthoritativeMobileAuthenticationSignedOutReason(reason);
    if (isAuthoritativeNativeSignOut) {
      // A real native logout is a security boundary, not a restoration race.
      // Clear any provisional timer and the WebView session immediately so a
      // later stale token cannot cancel or outlive the logout.
      void mobileAuthenticationSignOutCoordinator.signOutImmediately(
        performAuthoritativeMobileAuthenticationSignOut
      );
      confirmNativeState();
      return;
    }

    mobileAuthenticationSignOutCoordinator.requestSignOut(performProvisionalNativeEmptyStateSignOut);
    confirmNativeState();
  };

  const listenForEventsFromMobileApp = (jungleAppEvent: SendOrReceiveMobileAppEvent) => {
    switch (jungleAppEvent?.typeEvent) {
      case EventReceivedFromMobileApp.INTERFACE_LANGUAGE_CONTEXT:
        receiveNativeInterfaceLanguageContext(jungleAppEvent?.message);
        break;
      case EventReceivedFromMobileApp.INIT_APP:
        setIsIOS(jungleAppEvent?.message === "ios");
        setIsAndroid(jungleAppEvent?.message === "android");
        setIsRunningAsMobileApp(true);
        break;
      case EventReceivedFromMobileApp.APP_BUILD_INFO: {
        const nativeClientBuildInfo = parseNativeClientBuildInfoMessage(jungleAppEvent?.message);
        if (nativeClientBuildInfo) {
          applyNativeClientBuildInfoToTelemetry(nativeClientBuildInfo);
        }
        break;
      }
      case EventReceivedFromMobileApp.IOS_VERSION_PUBLISHED:
        setIosVersionPublished(jungleAppEvent?.message ? Number(jungleAppEvent.message) : 0);
        break;
      case EventReceivedFromMobileApp.ANDROID_VERSION_PUBLISHED:
        setAndroidVersionPublished(jungleAppEvent?.message ? Number(jungleAppEvent.message) : 0);
        break;
      case EventReceivedFromMobileApp.AUTHENTICATION_TOKEN:
        void handleMobileAuthenticationMessage(jungleAppEvent?.message);
        break;
      case EventReceivedFromMobileApp.AUTHENTICATION_SIGNED_OUT:
        void handleMobileAuthenticationSignedOut(jungleAppEvent?.message);
        break;
      case EventReceivedFromMobileApp.PAYMENT_SUCCESS:
        navigate("/subscription-successful");
        break;
      case EventReceivedFromMobileApp.PAYMENT_ERROR:
        navigate("/");
        break;
      case EventReceivedFromMobileApp.PAYMENT_CANCELED:
        navigate("/");
        break;
      case EventReceivedFromMobileApp.SUBSCRIPTION_UPDATED:
        if (jungleAppEvent?.message) {
          try {
            const updatedUserFromMobileApp = JSON.parse(jungleAppEvent.message) as UserData;
            userStore.getState().setCurrentUser(updatedUserFromMobileApp);
          } catch (error) {
            console.error("Failed to parse subscription_updated payload from mobile app:", error);
          }
        }
        break;
      case EventReceivedFromMobileApp.INSTALL_ATTRIBUTION:
      case EventReceivedFromMobileApp.REQUEST_INSTALL_ATTRIBUTION_ID_TOKEN: {
        // The Android app's install attribution (the Play install referrer's ad params), and its request for
        // the web's ID token (email sessions exist only in this WebView). Handled after startup
        // (install-attribution-bridge.ts); a token request left unanswered times out in the app.
        const { typeEvent: installAttributionEventType, message: installAttributionMessage } = jungleAppEvent;
        void loadInstallAttributionBridgeModule()
          .then(({ handleInstallAttributionBridgeEvent }) =>
            handleInstallAttributionBridgeEvent(installAttributionEventType, installAttributionMessage, auth)
          )
          .then((reply) => {
            if (reply) sendEventsToMobileApp(EventSentToMobileApp.INSTALL_ATTRIBUTION_ID_TOKEN, JSON.stringify(reply));
          })
          .catch(() => {
            // Best effort: attribution must never break the bridge.
          });
        break;
      }
      case EventReceivedFromMobileApp.BACK_ACTION:
        if (window.history.length > 2) {
          window.history.back();
        } else {
          navigate("/");
        }
        break;
      case EventReceivedFromMobileApp.SEND_SUBSCRIPTION_PRICES_TO_WEB:
        if (isIOS) {
          const rawPriceDetails = JSON.parse(jungleAppEvent?.message) as AppleSubscriptionProductDetails[];
          setAppleSubscriptionPriceDetails(rawPriceDetails);
          return;
        }
        const rawPriceDetails = JSON.parse(jungleAppEvent?.message) as GooglePlaySubscriptionProductDetails;
        setGooglePlaySubscriptionPriceDetails(rawPriceDetails);
        break;
      case EventReceivedFromMobileApp.PUSH_NOTIFICATION_OPENED:
        if (pushNotificationOpenedBridgeEventsAlreadyHandled.has(jungleAppEvent)) break;
        pushNotificationOpenedBridgeEventsAlreadyHandled.add(jungleAppEvent);
        handleMobilePushNotificationOpenedMessage(
          jungleAppEvent?.message,
          (deliveryId) => sendEventsToMobileApp(EventSentToMobileApp.PUSH_NOTIFICATION_OPEN_RECEIVED, deliveryId),
          navigate
        );
        break;
      default:
        break;
    }
  };

  //functions
  const onUploadAFileClicked = async (
    fileUploadInputRef: React.RefObject<HTMLInputElement> | null,
    setIsFileGettingDataUploaded: (isFileGettingDataUploaded: boolean) => void,
    setErrorMessage: (errorMessage: string | null) => void,
    filesToRead?: File[],
    translate: FileUploadTranslate = (_key, fallback) => fallback,
    onUploadBatchControlsChange?: FileUploadBatchControlsChange
  ) => {
    const haveCardsBeenGeneratedBefore = localStorage.getItem("haveCardsBeenGeneratedBefore");
    if (haveCardsBeenGeneratedBefore && !user) {
      setShowModalSignUpAndLogin(
        SignUpAndOnboardingModalView.SignInOrSignUpOptionsView,
        SignUpAndOnboardingModalActivatedReason.GeneratingSecondSetOfCards
      );
      return;
    }

    setShouldShowSelectImagesForOcclusionView(false);
    if (fileUploadInputRef?.current) {
      const inputElement = fileUploadInputRef.current;
      inputElement.onchange = async (event: Event) => {
        const target = event.target as HTMLInputElement;
        const files = Array.from(target.files || []);
        void processMultipleFiles(
          files,
          setIsFileGettingDataUploaded,
          setErrorMessage,
          undefined,
          "file-picker",
          translate,
          onUploadBatchControlsChange
        );
      };
      // Let a user retry the exact same file after a failure. Browsers do not
      // dispatch `change` for an unchanged file input value.
      inputElement.value = "";
      inputElement.click();
    } else {
      void processMultipleFiles(
        filesToRead || [],
        setIsFileGettingDataUploaded,
        setErrorMessage,
        undefined,
        "drag-and-drop",
        translate,
        onUploadBatchControlsChange
      );
    }
  };

  const processMultipleFiles = async (
    filesToRead: File[],
    setIsFileGettingDataUploaded: (isFileGettingDataUploaded: boolean) => void,
    setErrorMessage: (errorMessage: string | null) => void,
    onFirstFileIsReadyToGenerateQuestions?: () => void,
    inputMethod: FileUploadInputMethod = "unknown",
    translate: FileUploadTranslate = (_key, fallback) => fallback,
    onUploadBatchControlsChange?: FileUploadBatchControlsChange,
    lectureSourceContext?: LectureSourceUploadContext
  ) => {
    // Maximum 5 files at once
    const fileCountError = validateSelectedFileCount(filesToRead);
    if (fileCountError) {
      if (filesToRead[0]) {
        reportDocumentUploadFailure({
          error: fileCountError,
          attemptId: createUploadAttemptId(),
          inputMethod,
          file: filesToRead[0],
          contentMediumType: null,
          user,
        });
      }
      setErrorMessage(
        translate(
          "document_upload_error_too_many_files",
          "you can upload a maximum of 5 files at once. please select 5 or fewer files."
        )
      );
      return;
    }
    if (filesToRead.length === 0) {
      onUploadBatchControlsChange?.(null);
      return;
    }

    // A picker retry starts a new batch; retain partial-rejection guidance for drag-and-drop.
    if (inputMethod === "file-picker") setErrorMessage(null);

    // Set loading state to true when files are selected
    if (filesToRead.length > 0) {
      setIsFileGettingDataUploaded(true);
      // Set content medium type for the first file to show appropriate loading message
      const firstFile = filesToRead[0];
      const contentMediumType = getContentMediumTypeFromFileNameOrPageUrl(firstFile.name, firstFile.type);
      if (contentMediumType !== ContentMediumType.Webpage) {
        setCurrentContentMediumType(contentMediumType);
      }
    }

    const filesToGeneration: FileUploadWithDeckData[] = filesToRead.map((file, uploadedFileIndex) => ({
      file,
      lectureSourceContext,
      // Every accepted entry is queued immediately. Mark the whole batch as
      // active before starting any one file so an early synchronous rejection
      // cannot hide the spinner for files whose callbacks have not run yet.
      isBeingRead: true,
      hasUploadError: false,
      uploadErrorMessage: null,
      associatedDeck: null,
      uploadedFileIndex,
    }));
    const uploadBatchAbortController = new AbortController();
    let hasBatchControlBeenUsed = false;
    let areBatchRecoveryControlsEnabled = true;
    const publishUploadBatchControls = () => {
      const recoveryAvailability = getFileUploadBatchRecoveryAvailability(
        filesToGeneration.length,
        !areBatchRecoveryControlsEnabled
      );
      onUploadBatchControlsChange?.({
        cancel: cancelUploadBatch,
        retry: retryUploadBatch,
        ...recoveryAvailability,
      });
    };
    const cancelUploadBatch = () => {
      if (hasBatchControlBeenUsed || !areBatchRecoveryControlsEnabled) return;
      hasBatchControlBeenUsed = true;
      areBatchRecoveryControlsEnabled = false;
      setDocumentsAndUrlsForCardGenerationArray(markFileUploadBatchAsInactive(filesToGeneration));
      uploadBatchAbortController.abort("user-cancelled-upload");
      setLoadingPercentage(0);
      setIsFileGettingDataUploaded(false);
      onUploadBatchControlsChange?.(null);
    };
    const retryUploadBatch = () => {
      if (hasBatchControlBeenUsed || !areBatchRecoveryControlsEnabled || filesToGeneration.length !== 1) return;
      hasBatchControlBeenUsed = true;
      areBatchRecoveryControlsEnabled = false;
      uploadBatchAbortController.abort("user-retried-upload");
      setLoadingPercentage(0);
      const filesStillBeingRead = filesToGeneration
        .filter((fileUploadWithDeckData) => fileUploadWithDeckData.isBeingRead)
        .map((fileUploadWithDeckData) => fileUploadWithDeckData.file);
      if (filesStillBeingRead.length === 0) {
        setIsFileGettingDataUploaded(false);
        onUploadBatchControlsChange?.(null);
        return;
      }
      void processMultipleFiles(
        filesStillBeingRead,
        setIsFileGettingDataUploaded,
        setErrorMessage,
        onFirstFileIsReadyToGenerateQuestions,
        inputMethod,
        translate,
        onUploadBatchControlsChange,
        lectureSourceContext
      );
    };
    publishUploadBatchControls();

    setCurrentInputIndexInDocumentsAndUrlsArray(0);
    // Publish the shared array before any asynchronous callback can mutate an
    // entry or recompute the aggregate loading state.
    setDocumentsAndUrlsForCardGenerationArray(filesToGeneration);
    await Promise.all(
      filesToGeneration.map((fileUploadWithDeckData, fileToGenerateIndex) =>
        readFileContent(
          fileUploadWithDeckData,
          fileToGenerateIndex === 0,
          setIsFileGettingDataUploaded,
          setErrorMessage,
          onFirstFileIsReadyToGenerateQuestions,
          inputMethod,
          translate,
          uploadBatchAbortController.signal,
          () => {
            if (!areBatchRecoveryControlsEnabled) return;
            areBatchRecoveryControlsEnabled = false;
            publishUploadBatchControls();
          }
        )
      )
    );
    if (!uploadBatchAbortController.signal.aborted) {
      onUploadBatchControlsChange?.(null);
    }
  };

  const publishCurrentDocumentUploadState = () => {
    const currentFiles = useReviewDeckStore.getState().documentsAndUrlsForCardGenerationArray;
    if (Array.isArray(currentFiles)) {
      // Entries are shared with the in-flight callbacks. Publish a new array
      // reference after a field changes so generation-page selectors update.
      setDocumentsAndUrlsForCardGenerationArray([...currentFiles]);
    }
  };

  const readFileContent = async (
    fileUploadWithDeckData: FileUploadWithDeckData,
    isFirstFile: boolean,
    setIsFileGettingDataUploaded: (isFileGettingDataUploaded: boolean) => void,
    setGlobalErrorMessage: (errorMessage: string | null) => void,
    onFirstFileIsReadyToGenerateQuestions?: () => void,
    inputMethod: FileUploadInputMethod = "unknown",
    translate: FileUploadTranslate = (_key, fallback) => fallback,
    cancellationSignal?: AbortSignal,
    onStorageUploadCompleted?: () => void
  ) => {
    const updateCurrentFileReadingState = (isBeingRead: boolean) => {
      const effectiveIsBeingRead = cancellationSignal?.aborted ? false : isBeingRead;
      fileUploadWithDeckData.isBeingRead = effectiveIsBeingRead;
      publishCurrentDocumentUploadState();

      const currentFiles = useReviewDeckStore.getState().documentsAndUrlsForCardGenerationArray;
      setIsFileGettingDataUploaded(effectiveIsBeingRead || areAnyFilesBeingRead(currentFiles));
    };

    updateCurrentFileReadingState(true);
    let file = fileUploadWithDeckData.file;
    let shouldAutoGenerateQuestions = false;
    const haveCardsBeenGeneratedBefore = localStorage.getItem("haveCardsBeenGeneratedBefore");
    if ((!user && !haveCardsBeenGeneratedBefore) || user?.number_of_sets_of_cards_generated === 0) {
      shouldAutoGenerateQuestions = true;
    }

    if (haveCardsBeenGeneratedBefore && !user) {
      setShowModalSignUpAndLogin(
        SignUpAndOnboardingModalView.SignInOrSignUpOptionsView,
        SignUpAndOnboardingModalActivatedReason.GeneratingSecondSetOfCards
      );
      // Reset loading state when user needs to sign in
      updateCurrentFileReadingState(false);
      return;
    }
    let filename = file?.name;

    let contentMediumType: ContentMediumType | null = null;
    contentMediumType = getContentMediumTypeFromFileNameOrPageUrl(filename, file?.type);
    if (mimeTypesFiles.includes(file.type)) {
      contentMediumType = ContentMediumType.Video;
      if (file.type.includes("audio")) {
        contentMediumType = ContentMediumType.Audio;
      }
    }
    // this method returns a webpage by default but we know this isn't a webpage so we'll set it to null and check content type again in the method below
    if (contentMediumType === ContentMediumType.Webpage) {
      contentMediumType = null;
    }
    if (contentMediumType) {
      fileUploadWithDeckData.isBeingRead = true;
      await readFile(
        file,
        (errorMessage: string | null) => {
          if (errorMessage) {
            markFileUploadAsFailed(fileUploadWithDeckData, errorMessage);
            publishCurrentDocumentUploadState();
            // Surface the first error to the home-page banner as well
            if (isFirstFile) {
              setGlobalErrorMessage(errorMessage);
            }
          }
        },
        user,
        contentMediumType,
        updateCurrentFileReadingState,
        // The primary file owns the upload screen and its navigation hand-off.
        isFirstFile ? setLoadingPercentage : undefined,
        async (deck: DeckObject) => {
          fileUploadWithDeckData.associatedDeck = deck;
          publishCurrentDocumentUploadState();
          legacyReduxStore.getState().setIsRegeneratingQuestions(false);
          setSelectedOcclusionImagesWithBoxPositions(null);
          if (isFirstFile) {
            if (onFirstFileIsReadyToGenerateQuestions) {
              onFirstFileIsReadyToGenerateQuestions();
            } else {
              navigate(shouldAutoGenerateQuestions ? "/generate-questions/?autoGenerate=true" : "/generate-questions");
            }
          }
        },
        inputMethod,
        translate,
        cancellationSignal,
        onStorageUploadCompleted,
        fileUploadWithDeckData.lectureSourceContext
      );
    } else {
      reportDocumentUploadFailure({
        error: new FileUploadError({
          message: "The selected file type is not supported",
          stage: FileUploadStage.ValidateInput,
          code: FileUploadErrorCode.UnsupportedFile,
          retryable: false,
        }),
        attemptId: createUploadAttemptId(),
        inputMethod,
        file,
        contentMediumType: null,
        user,
      });
      void trackEvent(
        "Attempt to Upload Unsupported File Type",
        { fileType: getUnsupportedUploadFileType(file), inputMethod },
        user,
        null
      );
      const unsupportedFileMessage = getUnsupportedFileUploadMessage(file, translate);
      markFileUploadAsFailed(fileUploadWithDeckData, unsupportedFileMessage);
      publishCurrentDocumentUploadState();
      if (isFirstFile) {
        setGlobalErrorMessage(unsupportedFileMessage);
      }
      updateCurrentFileReadingState(false);
    }
  };
  async function createStripeCheckoutSession(
    user: any,
    subscriptionPlanTypeToUse: UserSubscriptionPlan,
    subscriptionPeriodType: UserSubscriptionTimePeriod,
    shouldReturnStripeUrl?: boolean,
    isCallerCurrent: () => boolean = () => true,
  ) {
    const authority = createBillingAuthority(user, isCallerCurrent);
    try {
      if (!user?.id || !authority.isCurrent()) return null;
      // Map the plan enum → the snake_case value the worker's /create-checkout-session
      // expects. ELITE must map to "elite" (it used to fall through to "mega_mind",
      // which silently created the wrong subscription for Free→Elite checkouts).
      const subscriptionPlanToUseString =
        subscriptionPlanTypeToUse === UserSubscriptionPlan.SUPER_LEARNER
          ? "super_learner"
          : subscriptionPlanTypeToUse === UserSubscriptionPlan.ELITE
            ? "elite"
            : "mega_mind";

      const isSubscriptionAnnual = subscriptionPeriodType === UserSubscriptionTimePeriod.ANNUAL;
      const subscriptionPeriodString = subscriptionPeriodType; // "annual" or "monthly"

      trackEvent("Upgrade: Creating Stripe Checkout Session", {}, user);
      logFirebaseAnalyticsEvent("checkout_session_initiated", {
        userId: user?.id,
        userEmail: user?.email_auth,
        subscriptionPlanTypeToUse,
        subscriptionPeriodType: subscriptionPeriodString,
      });

      // Meta browser ids and page for the server-side Purchase (contract C5); the Worker stores them with the
      // request's IP address and user agent, at once when the request carries the user's Firebase ID token.
      // Their reader loads after startup; if it cannot, checkout still goes on with the pixel's own cookies and
      // the page (without its query). Checkout never waits long for, or fails without, the token.
      const [metaCheckoutSessionFields, checkoutAuthorizationHeaders] = await Promise.all([
        loadMetaConversionTrackingModule()
          .then(({ getMetaCheckoutSessionFields }) => getMetaCheckoutSessionFields())
          .catch(() => ({
            fbp_cookie: getCookie("_fbp"),
            fbc_cookie: getCookie("_fbc"),
            event_source_url: `${window.location.origin}${window.location.pathname}`,
          })),
        getCheckoutAuthorizationHeadersIfAvailable(),
      ]);
      if (!authority.isCurrent()) return null;

      const createStripeCheckoutResponse: any = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/create-checkout-session`, {
        method: "POST",
        body: JSON.stringify({
          user_id: user.id,
          user_email: user.email_auth,
          is_subscription_annual: isSubscriptionAnnual,
          subscription_period_type: subscriptionPeriodString, // Send the actual period type
          guarantee_experiment_variant: isSubscriptionAnnual ? YEARLY_CHECKOUT_GUARANTEE_VARIANT : null,
          subscription_plan_type: subscriptionPlanToUseString,
          tolt_referral_id: toltReferralId,
          ...metaCheckoutSessionFields,
          stripe_checkout_cancel_or_return_url: window.location.href,
          did_subscribe_through_stripe_from_mobile_app: isRunningAsMobileApp,
        }),
        headers: {
          "Content-Type": "application/json",
          ...checkoutAuthorizationHeaders,
        },
      }).catch((error) => {
        if (!authority.isCurrent()) return;
        trackEvent(
          "Error: Failed to create Stripe Checkout Session",
          { error: error, errorMessage: error?.message },
          user
        );
        throw error;
      });
      if (!authority.isCurrent()) return null;

      if (createStripeCheckoutResponse?.status === 200) {
        // The Worker returns a hosted Checkout URL as text on every platform.
        const stripeCheckoutUrl = await createStripeCheckoutResponse.text();
        if (!authority.isCurrent()) return null;
        if (typeof stripeCheckoutUrl !== "string" || !stripeCheckoutUrl.trim()) throw new Error("Stripe checkout returned an empty URL");
        if (shouldReturnStripeUrl) {
          return stripeCheckoutUrl;
        } else {
          // Keep mobile checkout inside its WebView and direct /upgrade links in the current tab.
          const shouldNavigateInCurrentWindow = isRunningAsMobileApp || Boolean(window.ReactNativeWebView) ||
            window.location.pathname === "/upgrade";
          if (shouldNavigateInCurrentWindow) {
            window.location.href = stripeCheckoutUrl;
          } else {
            const stripeCheckoutPopupWindow = window.open(stripeCheckoutUrl, "_blank");
            // If the popup window fails to open (e.g., blocked by the browser — especially in Safari),
            // redirect the user directly to the Stripe Checkout URL as a fallback.
            if (!stripeCheckoutPopupWindow) {
              window.location.href = stripeCheckoutUrl;
            }
          }
        }
      } else {
        trackEvent(
          "Error: Failed to create Stripe Checkout Session",
          { errorStatus: createStripeCheckoutResponse?.status },
          user
        );
        throw new Error(`Stripe checkout failed (${createStripeCheckoutResponse?.status ?? "no response"})`);
      }

      return null;
    } finally {
      authority.dispose();
    }
  }

  /**
   * The exam pass's one-time Stripe checkout (the first upsell's T6, web only): the Worker prices it by `tier` and
   * ends it on the date the page printed, the end of a local day in this browser's time zone. Resolves with the
   * checkout's address, which the caller opens once its tear animation is done; throws on any failure.
   */
  async function createExamPassCheckoutSession(user: any, examPassTier: string): Promise<string> {
    if (!user?.id) throw new Error("exam pass checkout needs a signed-in account");
    trackEvent("Upgrade: Creating Stripe Checkout Session", { purchase_type: "exam_pass" }, user);
    const [metaCheckoutSessionFields, checkoutAuthorizationHeaders] = await Promise.all([
      loadMetaConversionTrackingModule()
        .then(({ getMetaCheckoutSessionFields }) => getMetaCheckoutSessionFields())
        .catch(() => ({
          fbp_cookie: getCookie("_fbp"),
          fbc_cookie: getCookie("_fbc"),
          event_source_url: `${window.location.origin}${window.location.pathname}`,
        })),
      getCheckoutAuthorizationHeadersIfAvailable(),
    ]);
    let timeZone: string | undefined;
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      timeZone = undefined;
    }
    const checkoutResponse = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/create-checkout-session`, {
      method: "POST",
      body: JSON.stringify({
        user_id: user.id,
        user_email: user.email_auth,
        purchase_type: "exam_pass",
        exam_pass_tier: examPassTier,
        time_zone: timeZone,
        tolt_referral_id: toltReferralId,
        ...metaCheckoutSessionFields,
        stripe_checkout_cancel_or_return_url: window.location.href,
      }),
      headers: { "Content-Type": "application/json", ...checkoutAuthorizationHeaders },
    });
    if (checkoutResponse.status !== 200) {
      throw new Error(`exam pass checkout failed (${checkoutResponse.status})`);
    }
    const checkoutUrl = await checkoutResponse.text();
    if (typeof checkoutUrl !== "string" || !checkoutUrl.startsWith("https://")) {
      throw new Error("exam pass checkout returned no address");
    }
    return checkoutUrl;
  }

  const onClickSubscriptionUpgradeButton = async (
    user: UserData,
    subscriptionPlanToUpgradeTo: UserSubscriptionPlan,
    subscriptionPeriodType: UserSubscriptionTimePeriod,
    subscriptionPricingDetails: any,
    isCallerCurrent: () => boolean = () => true,
    onPurchaseRouteResolved?: (route: SubscriptionPurchaseRoute) => void,
    isRouteObservationCurrent: () => boolean = isCallerCurrent,
  ) => {
    const authority = createBillingAuthority(user, isCallerCurrent);
    const generation = billingLifetime.current.generation;
    let accountChanged = userStore.getState().currentUser?.id !== user?.id;
    const unsubscribe = userStore.subscribe((state) => {
      if (state.currentUser?.id !== user?.id) accountChanged = true;
    });
    const canObserveRoute = () => !accountChanged && billingLifetime.current.mounted &&
      billingLifetime.current.generation === generation && isRouteObservationCurrent();
    try {
      if (!user?.id || !authority.isCurrent()) return null;
      const { userSubscriptionPlan: currentUserSubscriptionPlan, managerSubscriptionAccount } =
        getUserSubscriptionPlanAndLimits(user);
      const { hasSubscriptionBinding } = getSubscriptionBillingIdentity(user?.subscription_information);

      const isRunningAsMobileApp = pageStore.getState().isRunningAsMobileApp;

      // Detect user's current country for subscription logic (only if not already detected)
      let currentCountry = currentCountryUserIsIn;
      if (currentCountry === null) {
        currentCountry = await detectUserCountry();
        if (!canObserveRoute()) return null;
        setCurrentCountryUserIsIn(currentCountry);
      }
      // The paywall decides whether to show the yearly guarantees (Stripe checkout and Google Play, never the
      // App Store) with this same function, so what it promises and where the purchase is paid can't disagree.
      const subscriptionPurchaseRoute = getSubscriptionPurchaseRoute({
        isRunningAsMobileApp,
        isAndroid,
        isIOS,
        currentCountryUserIsIn: currentCountry,
        currentUserSubscriptionPlan,
        managerSubscriptionAccount,
        hasSubscriptionBinding,
        hasUserSubscribedBefore: hasUserSubscribedBefore(user),
      });
      // Country detection can resolve after the picker starts waiting. Publish
      // the actual route before dispatching a payment request or native event.
      // An expected first binding may revoke submission while its paywall still
      // needs this read-only route result to recognize the completed purchase.
      if (!canObserveRoute()) return null;
      onPurchaseRouteResolved?.(subscriptionPurchaseRoute);
      if (!authority.isCurrent()) return null;

      if (subscriptionPurchaseRoute === SubscriptionPurchaseRoute.SubscriptionManagementUnavailable) {
        showAlertForUnrecognizedSubscriptionBilling();
        return null;
      }
      if (subscriptionPurchaseRoute === SubscriptionPurchaseRoute.ManageWhereTheSubscriptionWasBought) {
        showAlertForUpgradeAccountOnAppleAndAndroid(managerSubscriptionAccount);
        return null;
      }

      // handle case for if user is on mobile app
      if (isRunningAsMobileApp) {
        const currentSubscriptionPeriodType =
          user?.subscription_information?.subscription_period_type === "annual"
            ? UserSubscriptionTimePeriod.ANNUAL
            : UserSubscriptionTimePeriod.MONTHLY;

        const routeMobileStorePlanChange = async () => {
          if (!authority.isCurrent()) return;
          const shouldUpgradeThroughInAppPurchase = isMobileSubscriptionUpgrade(
            currentUserSubscriptionPlan,
            subscriptionPlanToUpgradeTo,
            currentSubscriptionPeriodType,
            subscriptionPeriodType
          );
          if (shouldUpgradeThroughInAppPurchase) {
            await startMobileStoreCheckout({
              user,
              subscriptionPlanToUpgradeTo,
              subscriptionPeriodType,
              subscriptionPricingDetails,
              isAndroid,
              sendEventsToMobileApp,
              isSubscriptionUpgrade: true,
              isRequestCurrent: authority.isCurrent,
            });
          } else {
            sendEventsToMobileApp(EventSentToMobileApp.OPEN_PLATFORM_SUBSCRIPTION_MANAGEMENT);
          }
        };

        // Apple/Google subscribers: upgrade via IAP (immediate); downgrades → platform manager.
        if (subscriptionPurchaseRoute === SubscriptionPurchaseRoute.NativeStorePlanChange) {
          await routeMobileStorePlanChange();
          return null;
        }

        // First-time purchase on Android or iOS (outside USA) via native IAP.
        if (
          subscriptionPurchaseRoute === SubscriptionPurchaseRoute.GooglePlayInAppPurchase ||
          subscriptionPurchaseRoute === SubscriptionPurchaseRoute.AppStoreInAppPurchase
        ) {
          await startMobileStoreCheckout({
            user,
            subscriptionPlanToUpgradeTo,
            subscriptionPeriodType,
            subscriptionPricingDetails,
            isAndroid,
            sendEventsToMobileApp,
            isSubscriptionUpgrade: false,
            isRequestCurrent: authority.isCurrent,
          });
          return null;
        }

        // Only a recognized Stripe binding may open the billing portal.
        try {
          let stripeCheckoutUrl: string | null = null;

          if (managerSubscriptionAccount === ManagerSubscriptionAccount.Stripe && hasUserSubscribedBefore(user)) {
            // Get Stripe management URL for existing subscribers
            stripeCheckoutUrl = await getStripeManageAccountSessionForUser(user, true, authority.isCurrent);
          } else {
            // Create Stripe checkout session for new subscribers
            stripeCheckoutUrl = await createStripeCheckoutSession(
              user,
              subscriptionPlanToUpgradeTo,
              subscriptionPeriodType,
              true, // shouldReturnStripeUrl
              authority.isCurrent,
            );
          }

          if (stripeCheckoutUrl && authority.isCurrent()) {
            window.location.href = stripeCheckoutUrl;
          }
        } catch (error) {
          if (!authority.isCurrent()) return null;
          trackEvent(
            "Error: Failed to generate Stripe URL for mobile app",
            {
              error: error,
              errorMessage: error?.message,
            },
            user
          );
          // Keep recovery with the billing owner that selected this request.
          // The picker clears its waiting state and allows an explicit retry.
          throw error;
        }
        return null;
      }

      if (managerSubscriptionAccount === ManagerSubscriptionAccount.Stripe && hasUserSubscribedBefore(user)) {
        const urlForManagingStripeSubscription = await getStripeManageAccountSessionForUser(user, true, authority.isCurrent);
        if (authority.isCurrent()) openNewUrlPath(urlForManagingStripeSubscription);
      } else {
        await createStripeCheckoutSession(user, subscriptionPlanToUpgradeTo, subscriptionPeriodType, undefined, authority.isCurrent);
      }
    } catch (error) {
      if (!authority.isCurrent()) return null;
      throw error;
    } finally {
      authority.dispose();
      unsubscribe();
    }
  };

  // ── Pro → Elite mid-plan upgrade (Stripe proration) ──────────────────────────
  // Two thin wrappers around the Cloudflare worker's proration endpoints (built in
  // the worker side of Phase 5.2). Both REQUIRE a Firebase ID token in the
  // Authorization header — the worker verifies token.sub === user_id (guard C1) so
  // a user can't preview/charge someone else's subscription. The
  // QuestionQualityUpsellModal calls preview first (to show the real prorated
  // amount), then commit on confirm. See spec §5.4.
  const getProToEliteUpgradePreview = async (
    user: UserData,
    targetPeriodType: "monthly" | "annual",
    isCallerCurrent: () => boolean = () => true,
  ): Promise<
    { status: "ok"; proratedAmountInCents?: number; currency?: string } | { status: "error"; errorCode: string }
  > => {
    const authority = createBillingAuthority(user, isCallerCurrent);
    try {
      if (!authority.isCurrent()) return { status: "error", errorCode: "request_cancelled" };
      const billing = getSubscriptionBillingIdentity(user?.subscription_information);
      if (!user?.id || !billing.hasRecognizedBillingBinding || billing.managerSubscriptionAccount !== ManagerSubscriptionAccount.Stripe) {
        return { status: "error", errorCode: "stripe_binding_required" };
      }
      const idToken = (await getSignedInFirebaseUser()?.getIdToken(false)) ?? null;
      if (!authority.isCurrent()) return { status: "error", errorCode: "request_cancelled" };
      if (!idToken) return { status: "error", errorCode: "missing_auth_token" };
      const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/payments/preview_pro_to_elite_upgrade_cost`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ user_id: user.id, target_period_type: targetPeriodType }),
      });
      if (!authority.isCurrent()) return { status: "error", errorCode: "request_cancelled" };
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        if (!authority.isCurrent()) return { status: "error", errorCode: "request_cancelled" };
        return { status: "error", errorCode: errorBody?.error ?? "stripe_preview_failed" };
      }
      const body = await response.json();
      if (!authority.isCurrent()) return { status: "error", errorCode: "request_cancelled" };
      return { status: "ok", proratedAmountInCents: body?.prorated_amount_in_cents, currency: body?.currency };
    } catch {
      return { status: "error", errorCode: authority.isCurrent() ? "network_error" : "request_cancelled" };
    } finally {
      authority.dispose();
    }
  };

  const commitProToEliteUpgrade = async (
    user: UserData,
    targetPeriodType: "monthly" | "annual",
    isCallerCurrent: () => boolean = () => true,
  ): Promise<
    | { status: "ok" | "already_on_elite"; proratedAmountInCents?: number; currency?: string }
    | { status: "error"; errorCode: string }
  > => {
    const authority = createBillingAuthority(user, isCallerCurrent);
    try {
      if (!authority.isCurrent()) return { status: "error", errorCode: "request_cancelled" };
      const billing = getSubscriptionBillingIdentity(user?.subscription_information);
      if (!user?.id || !billing.hasRecognizedBillingBinding || billing.managerSubscriptionAccount !== ManagerSubscriptionAccount.Stripe) {
        return { status: "error", errorCode: "stripe_binding_required" };
      }
      const idToken = (await getSignedInFirebaseUser()?.getIdToken(false)) ?? null;
      if (!authority.isCurrent()) return { status: "error", errorCode: "request_cancelled" };
      if (!idToken) return { status: "error", errorCode: "missing_auth_token" };
      const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/payments/upgrade_existing_subscription_to_elite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ user_id: user.id, target_period_type: targetPeriodType }),
      });
      if (!authority.isCurrent()) return { status: "error", errorCode: "request_cancelled" };
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        if (!authority.isCurrent()) return { status: "error", errorCode: "request_cancelled" };
        return { status: "error", errorCode: errorBody?.error ?? "stripe_modify_failed" };
      }
      const body = await response.json();
      if (!authority.isCurrent()) return { status: "error", errorCode: "request_cancelled" };
      // Commit returns the charged currency under `prorated_amount_currency`
      // (preview uses `currency`); normalize to `currency` for the caller.
      return {
        status: body?.status === "already_on_elite" ? "already_on_elite" : "ok",
        proratedAmountInCents: body?.prorated_amount_in_cents,
        currency: body?.prorated_amount_currency,
      };
    } catch {
      return { status: "error", errorCode: authority.isCurrent() ? "network_error" : "request_cancelled" };
    } finally {
      authority.dispose();
    }
  };

  const snapshotStreakAndExamModelByUserId = () => {
    if (
      user?.id &&
      isRunningAsMobileApp &&
      user?.features_user_has_seen_in_whats_new_list?.includes(NewFeaturesForWhatsNewModal.WidgetSetup) &&
      user?.user_settings?.has_widget_been_configured
    ) {
      const streakCollectionRef = doc(db, STREAKS_COLLECTION_NAME, user?.id);
      return onSnapshot(streakCollectionRef, (streakDataRef) => {
        if (streakDataRef.exists()) {
          sendEventsToMobileApp(EventSentToMobileApp.RELOAD_WIDGET_DATA);
        }
      });
    }
    return null;
  };

  return {
    rightArrowKeyAction,
    leftArrowKeyAction,
    getNumberOfUnderstandingCardsForNonAuthenticatedUser,
    generateQuestionsForPageUrl,
    sendEventsToMobileApp,
    onUploadAFileClicked,
    processMultipleFiles,
    onClickSubscriptionUpgradeButton,
    getProToEliteUpgradePreview,
    commitProToEliteUpgrade,
    createStripeCheckoutSession,
    createExamPassCheckoutSession,
    readFileContent,
    snapshotStreakAndExamModelByUserId,
    handleAfterReviewCompletedFlow,
  };
};

export const screenshotImageWithLabelBoxesForOcclusion = async (
  occlusionImageCardData: CardData,
  index: number,
  user: UserData
) => {
  try {
    // Creates a temporary invisible container in the DOM
    const container = document.createElement("div");
    container.style.position = "fixed"; // Position it relative to the viewport
    container.style.top = "0"; // Place at top
    container.style.left = "0"; // Place at left
    container.style.pointerEvents = "none"; // Prevent any mouse interactions
    container.style.opacity = "0"; // Make it invisible

    // Append to body
    document.body.appendChild(container);

    // Get the image URL and obtain the presigned URL
    const answerSideImageUrl = occlusionImageCardData.answer_side_image_url;
    const imageKey = answerSideImageUrl.split(".com/")[1];
    const decodedImageKey = decodeURIComponent(imageKey);
    const presignedUrl = await getPresignedUrlForImageKey(decodedImageKey);

    // image loading
    const response = await fetch(presignedUrl, {
      mode: "cors",
      headers: {
        "Access-Control-Allow-Origin": "*",
      },
    });

    // Create an object URL from the blob
    const imageBlob = await response.blob();
    const blobObjectUrl = URL.createObjectURL(imageBlob);

    // Create a new image and wait for it to load
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.crossOrigin = "anonymous";
      img.src = blobObjectUrl;
    });

    // Convert blob to Data URL
    const reader = new FileReader();
    const dataURL = await new Promise<string>((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(imageBlob);
    });

    // Clone the occlusionImageCardData and set image URL to Data URL
    const cardDataWithUpdatedURL = {
      ...occlusionImageCardData,
      question_side_image_url: dataURL,
    };

    // Render the component into the container with modified data
    const root = createRoot(container);
    root.render(
      <div id={`occlusion-image-${index}`}>
        <ImageAndLabelBoxesForOcclusionImage occlusionImageCardData={cardDataWithUpdatedURL} showAllLabels={true} />
      </div>
    );

    // Wait for images to load
    await new Promise((resolve) => setTimeout(resolve, 500)); // Adjust the timeout if needed

    // Find the element to screenshot
    const element = container.querySelector(`#occlusion-image-${index}`);

    // Use html2canvas on the element
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(element as HTMLElement, {
      backgroundColor: null, // Preserve transparency
      scale: 2, // Higher quality output
      logging: true,
      onclone: (document) => {
        const clonedElement = document.getElementById(`occlusion-image-${index}`);
        const clonedImg = clonedElement?.querySelector("img");
        if (clonedImg) {
          clonedImg.crossOrigin = "";
          clonedImg.src = blobObjectUrl;
        }
      },
    });

    // Clean up
    root.unmount();
    document.body.removeChild(container);

    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => resolve(blob as Blob), "image/png", 1.0);
    });

    // Create File object from blob
    const file = new File([blob], `occlusion-screenshot-${index}.png`, {
      type: "image/png",
    });

    // Get pre-signed URL
    const urlForFileUpload = await getUrlForFileUploadToS3(file.name, user, null);

    // Upload to S3
    const uploadStatus = await uploadFileToS3(file, urlForFileUpload, user, null);

    if (uploadStatus === "success") {
      const urlOfImageAndImageLabelBoxesForOcclusion = `${urlForFileUpload.url}${urlForFileUpload.fields.key}`;
      return urlOfImageAndImageLabelBoxesForOcclusion;
    } else {
      console.error("Failed to upload image to S3");
    }
  } catch (error) {
    trackEvent("Error: Failed to screenshotImageWithLabelBoxesForOcclusion", {
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  }
};

export const screenshotOcclusionImagesAndExportsToAnkiFile = async (
  allCardsForCurrentDeckAndSubdecks: CardData[],
  imageOcclusionCardsGroupedByImageURL: { [key: string]: CardData[] },
  currentReviewDeck: DeckObject | null,
  user: UserData,
  sourceOfClick: string
) => {
  const updatedAllCardsForCurrentDeckAndSubdecks = [...allCardsForCurrentDeckAndSubdecks];
  const screenshotImageWithLabelBoxesForOcclusionPromises = Object.keys(imageOcclusionCardsGroupedByImageURL).map(
    async (imageOcclusionDetails, index) => {
      const occlusionImageCardData = imageOcclusionCardsGroupedByImageURL[imageOcclusionDetails][0];
      const imageUrlToExportToAnki = await screenshotImageWithLabelBoxesForOcclusion(
        occlusionImageCardData,
        index,
        user
      );

      // Update all cards that share the same image URL
      const cardsToUpdateImageURLToExportToAnki = imageOcclusionCardsGroupedByImageURL[imageOcclusionDetails];
      cardsToUpdateImageURLToExportToAnki.forEach((card) => {
        const cardIndex = updatedAllCardsForCurrentDeckAndSubdecks.findIndex((c) => c.id === card.id);
        if (cardIndex !== -1) {
          updatedAllCardsForCurrentDeckAndSubdecks[cardIndex] = {
            ...updatedAllCardsForCurrentDeckAndSubdecks[cardIndex],
            image_url_to_export_to_anki: imageUrlToExportToAnki,
          };
        }
      });

      return imageUrlToExportToAnki;
    }
  );

  try {
    await Promise.all(screenshotImageWithLabelBoxesForOcclusionPromises);

    // Proceed with Anki export after screenshots are taken
    exportCardsToAnkiFile(updatedAllCardsForCurrentDeckAndSubdecks, currentReviewDeck?.name, user, sourceOfClick);
  } catch (error) {
    trackEvent("Error: Failed to screenshotOcclusionImagesAndExportsToAnkiFile", {
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  }
};
